import { z } from 'zod';

/** Locales shipped by the game. `en` is the source language. */
export const locales = ['en', 'zh-Hans', 'ja'] as const;
export type Locale = (typeof locales)[number];
export const sourceLocale: Locale = 'en';

const idPattern = (prefix: string) => new RegExp(`^${prefix}\\.[a-z0-9_]+$`);
const ref = (prefix: string) => z.string().regex(idPattern(prefix), `expected an ID like "${prefix}.some_name"`);

const int = z.number().int();
const stat = int.nonnegative();
/** Whole-number percent. 100 = 100%. Content never uses fractions as decimals (game-core is integer-only). */
const percent = int.min(0);
/** An exact fraction such as 1/3, kept as two integers so game-core can compute `value * num / den`. */
const fraction = z.strictObject({ num: int.positive(), den: int.positive() });
/** A fixed amount or a fraction of the relevant maximum. */
const quantity = z.union([z.strictObject({ amount: int.positive() }), fraction]);

// ---------------------------------------------------------------- shared vocabularies

/** Wu Xing five phases plus light, dark and imaginary. Effectiveness lives in data/rules/type-chart.yaml. */
export const elements = ['metal', 'fire', 'water', 'wood', 'earth', 'light', 'dark', 'imaginary'] as const;
export type Element = (typeof elements)[number];
/**
 * hp: health. ap: aetherial points, the resource aetherial skills spend.
 * patk/pdef: physical. aatk/adef: aetherial. spd: speed.
 * SP (the physical resource) is not a stat: it starts at 0 each battle and is built by basic attacks (see rules/combat.yaml).
 */
export const statNames = ['hp', 'ap', 'patk', 'pdef', 'aatk', 'adef', 'spd'] as const;
/** Stats that can be raised or lowered in stages during a battle. */
export const stageStats = ['patk', 'pdef', 'aatk', 'adef', 'spd'] as const;
export const styles = ['physical', 'aetherial'] as const;
/** `disrupt`: cannot charge, so cannot use aetherial skills that need a charge. */
export const statusNames = ['burn', 'poison', 'sleep', 'paralysis', 'confusion', 'stun', 'blind', 'disrupt'] as const;

/**
 * Effects are the one vocabulary shared by skills, items and passives. Adding a new
 * `type` here means adding a handler in game-core; that is a fundamental
 * change and needs an ADR. Combining existing effects needs no code.
 */
export const effectSchema = z.discriminatedUnion('type', [
  /**
   * `style` picks patk/pdef or aatk/adef. In a skill it defaults to the skill's style;
   * items and passives have no style of their own, so there it is required.
   */
  z.strictObject({ type: z.literal('damage'), power: int.positive(), element: z.enum(elements).optional(), style: z.enum(styles).optional() }),
  z.strictObject({ type: z.literal('heal'), ...{ hp: quantity } }),
  z.strictObject({ type: z.literal('restore_ap'), ap: quantity }),
  z.strictObject({ type: z.literal('status'), status: z.enum(statusNames), chance: percent.max(100), turns: int.positive() }),
  z.strictObject({ type: z.literal('cure'), statuses: z.array(z.enum(statusNames)).min(1).or(z.literal('all')) }),
  z.strictObject({ type: z.literal('stat_stage'), stat: z.enum(stageStats), stages: int.min(-6).max(6), turns: int.positive().optional() }),
  z.strictObject({ type: z.literal('exp'), amount: int.positive() }),
  z.strictObject({ type: z.literal('escape') }),
  /** Cancels a charge the target is holding. Ordinary damage does not. */
  z.strictObject({ type: z.literal('interrupt_charge') }),
  /** The user moves after the effect resolves. */
  z.strictObject({ type: z.literal('reposition'), to: z.enum(['adjacent_to_target']) }),
  /**
   * Brings a fallen party unit back with `hp` (an amount, or a fraction of its max HP). Only valid in a
   * charm's `auto` block for now: skills, item uses and passives cannot revive.
   */
  z.strictObject({ type: z.literal('revive'), hp: quantity }),
  /** Placeholder for an effect that is not modelled yet. Lint accepts it; game-core will not run it. */
  z.strictObject({ type: z.literal('custom'), key: z.string().min(1), note: z.string().optional() }),
]);
export type Effect = z.infer<typeof effectSchema>;

/** Refuses a `revive` effect outside a charm's `auto` block (the effects at `path`). */
const refuseRevive = (effects: readonly { type: string }[], path: string, ctx: z.RefinementCtx): void => {
  effects.forEach((e, i) => {
    if (e.type === 'revive') ctx.addIssue({ code: 'custom', path: [path, i], message: "revive is only valid in a charm's auto block" });
  });
};

/**
 * Targeting is defined on the dungeon grid. The user faces one of eight
 * directions; offsets are written for a user facing "up" ([0, 1]) and are
 * rotated to the actual facing by game-core. Distances are Manhattan
 * (a diamond: range 2 is the 1-3-5-3-1 shape), and a `single` target must be
 * reachable within `range` orthogonal steps that avoid walls (line of sight).
 */
export const targetingSchema = z
  .strictObject({
    /**
     * self:   the user only.
     * single: one unit within `range` tiles (1 = adjacent).
     * line:   flies in the facing direction (n, e, s or w only) up to `range` tiles and hits the first unit (or every unit, with `pierce`).
     * room:   every unit in the current room (or within `range` tiles in a corridor).
     * area:   every unit standing on one of `cells`, offsets relative to the user.
     * floor:  every unit on the floor.
     */
    shape: z.enum(['self', 'single', 'line', 'room', 'area', 'floor']),
    /** Who is affected. Omit for `self`. `allies` includes the user. */
    who: z.enum(['allies', 'enemies', 'everyone']).optional(),
    range: int.min(1).max(20).optional(),
    /** For `area`: [dx, dy] offsets, user at [0, 0] facing +y. */
    cells: z.array(z.tuple([int.min(-10).max(10), int.min(-10).max(10)])).min(1).optional(),
    /** For `line`: hit every unit along the line instead of stopping at the first. */
    pierce: z.boolean().optional(),
    /** For `line`: lost per tile beyond the first. `accuracy` in percent points, `power` in power points. */
    falloff: z.strictObject({ accuracy: int.min(0).optional(), power: int.min(0).optional() }).optional(),
  })
  .superRefine((t, ctx) => {
    const needsWho = t.shape !== 'self';
    if (needsWho && !t.who) ctx.addIssue({ code: 'custom', path: ['who'], message: `shape "${t.shape}" needs who` });
    if (!needsWho && t.who) ctx.addIssue({ code: 'custom', path: ['who'], message: 'shape "self" takes no who' });
    const needsRange = t.shape === 'single' || t.shape === 'line';
    if (needsRange && t.range === undefined) ctx.addIssue({ code: 'custom', path: ['range'], message: `shape "${t.shape}" needs range` });
    if (t.shape === 'area' && !t.cells) ctx.addIssue({ code: 'custom', path: ['cells'], message: 'shape "area" needs cells' });
    if (t.shape !== 'area' && t.cells) ctx.addIssue({ code: 'custom', path: ['cells'], message: 'only shape "area" takes cells' });
    if (t.shape !== 'line' && (t.pierce !== undefined || t.falloff)) {
      ctx.addIssue({ code: 'custom', path: ['pierce'], message: 'pierce and falloff apply only to shape "line"' });
    }
  });
export type Targeting = z.infer<typeof targetingSchema>;

// ---------------------------------------------------------------- skills

export const skillSchema = z
  .strictObject({
    id: ref('skill'),
    /** Omit for typeless skills: 1x against everything, but no damage to imaginary (rules/combat.yaml). */
    element: z.enum(elements).optional(),
    kind: z.enum(['attack', 'support', 'status']),
    /** Which attack and defence stats a damaging skill uses. Required when any effect deals damage. */
    style: z.enum(styles).optional(),
    targeting: targetingSchema,
    /** Aetherial skills normally take a charge turn (rules/combat.yaml). Set true for ones that fire at once. */
    noCharge: z.boolean().optional(),
    accuracy: percent.max(100).default(100),
    /** Resource spent per use. Aetherial skills spend AP, physical skills spend SP. Omit for free. */
    cost: z.strictObject({ ap: int.positive().optional(), sp: int.positive().optional() }).default({}),
    effects: z.array(effectSchema).min(1),
    tags: z.array(z.string()).default([]),
  })
  .superRefine((s, ctx) => {
    const dealsDamage = s.effects.some((e) => e.type === 'damage');
    if (dealsDamage && !s.style) ctx.addIssue({ code: 'custom', path: ['style'], message: 'damaging skills need style (physical | aetherial)' });
    if (s.style === 'physical' && s.cost.ap) ctx.addIssue({ code: 'custom', path: ['cost'], message: 'physical skills spend SP, not AP' });
    if (s.style === 'aetherial' && s.cost.sp) ctx.addIssue({ code: 'custom', path: ['cost'], message: 'aetherial skills spend AP, not SP' });
    if (s.noCharge && s.style !== 'aetherial') ctx.addIssue({ code: 'custom', path: ['noCharge'], message: 'only aetherial skills charge, so only they can skip it' });
    refuseRevive(s.effects, 'effects', ctx);
  });
export type Skill = z.infer<typeof skillSchema>;

// ---------------------------------------------------------------- passives

/**
 * A passive is a list of triggers: when X happens, if Y holds, do Z.
 * Provisional vocabulary: it covers the first batch and will grow with game-core.
 */
export const passiveTriggerSchema = z.strictObject({
  /**
   * always:           the `do` modifiers apply whenever `if` holds.
   * on_damage_taken:  after the holder takes damage from any source.
   * on_fatal_damage:  when a hit would reduce hp to 0; resolved before the holder falls.
   * turn_end:         at the end of the holder's turn.
   */
  when: z.enum(['always', 'on_damage_taken', 'on_fatal_damage', 'turn_end']),
  if: z
    .strictObject({
      hpBelow: fraction.optional(),
      hpFull: z.literal(true).optional(),
      damageElement: z.enum(elements).optional(),
      damageStyle: z.enum(styles).optional(),
      tookDamageThisTurn: z.literal(true).optional(),
    })
    .optional(),
  /** Fires at most once per battle. Being revived resets it. */
  oncePerBattle: z.boolean().optional(),
  do: z
    .array(
      z.discriminatedUnion('type', [
        // Ordinary effects, applied to the holder.
        ...effectSchema.options,
        // Modifiers, meaningful with `when: always`.
        z.strictObject({ type: z.literal('damage_taken_mod'), style: z.enum(styles).optional(), element: z.enum(elements).optional(), percent: int.min(-100).max(400) }),
        z.strictObject({ type: z.literal('healing_received_mod'), percent: int.min(-100).max(400) }),
        z.strictObject({ type: z.literal('survive_fatal'), hpLeft: int.positive().default(1) }),
        /** Skills of this style need no charge turn. */
        z.strictObject({ type: z.literal('skip_charge'), style: z.enum(styles) }),
      ]),
    )
    .min(1),
}).superRefine((t, ctx) => {
  t.do.forEach((d, i) => {
    if (d.type === 'damage' && d.style === undefined) {
      ctx.addIssue({ code: 'custom', path: ['do', i, 'style'], message: 'damage in a passive needs style (physical | aetherial)' });
    }
    // Escape is checked when the leader uses an item or skill; a passive fires mid-round, where it could not be refused.
    if (d.type === 'escape') ctx.addIssue({ code: 'custom', path: ['do', i], message: 'a passive cannot escape (only an item or skill the leader uses can)' });
  });
  refuseRevive(t.do, 'do', ctx);
});

export const passiveSchema = z.strictObject({
  id: ref('passive'),
  triggers: z.array(passiveTriggerSchema).min(1),
  tags: z.array(z.string()).default([]),
});
export type Passive = z.infer<typeof passiveSchema>;

// ---------------------------------------------------------------- monsters

const statBlock = z.strictObject({ hp: stat, ap: stat, patk: stat, pdef: stat, aatk: stat, adef: stat, spd: stat });
/** Base stats: as `statBlock`, but `hp` must be positive (the level formula keeps max HP positive anyway). */
const baseStatBlock = statBlock.extend({ hp: int.positive() });

export const monsterSchema = z.strictObject({
  id: ref('monster'),
  /** One or two types, Pokémon-style. The first is the primary type. */
  types: z.array(z.enum(elements)).min(1).max(2),
  rarity: z.enum(['common', 'uncommon', 'rare', 'legendary']).default('common'),
  /** Species base values; game-core derives the stats at a level from these (design page Combat rules, Stats). `hp` is positive. */
  baseStats: baseStatBlock,
  hatchMinutes: int.positive(),
  /** How many gene blocks this species can hold. */
  geneSlots: int.min(0).max(9),
  /** Skills learned by level. Level 1 entries are the starting kit. */
  learnset: z.array(z.strictObject({ level: int.min(1).max(100), skill: ref('skill') })).default([]),
  /** Skills this species can pass on through gene blocks. */
  geneSkills: z.array(ref('skill')).default([]),
  /** Passives this species can pass on through gene blocks. */
  genePassives: z.array(ref('passive')).default([]),
  /** Species passives. An individual has exactly one of these at a time (like Pokémon abilities). */
  passivePool: z.array(ref('passive')).min(1),
  tags: z.array(z.string()).default([]),
});
export type Monster = z.infer<typeof monsterSchema>;

// ---------------------------------------------------------------- items

export const itemCategories = [
  /** Used inside a dungeon: potions, throwables, seeds, ropes. */
  'consumable',
  /** Used on a monster outside a dungeon: stat boosters, gene materials, evolution items. */
  'monster_item',
  /** Consumed by an airship or town facility: fuel, feed, building materials. */
  'facility_item',
  /** Bought low, sold high. No use. */
  'trade_good',
  /** Crafting or breeding ingredient. No direct use. */
  'material',
  /** Story or unlock items. Cannot be sold. */
  'key',
  /** Equipped by a human: gives patk/aatk and the human's skills. Needs `equip`. */
  'weapon',
  /** Equipped by a human: gives pdef/adef. Needs `equip`. */
  'armor',
  /** Carried in the dungeon bag, acts by itself (cannot be used by hand): the Revive Orb. Needs `auto`. */
  'charm',
] as const;

/**
 * What an equipped item gives a human. `stats` are bases fed through the
 * stat formula at the item's own `level` (design page Combat rules, Stats), so
 * they do not grow with the wearer's level; better equipment is a better item.
 * A weapon sets only `patk`/`aatk` and grants `skills` (the human's whole
 * skill list, where its elemental damage comes from: a human's basic attack is
 * typeless); an armor sets only `pdef`/`adef`. A stat left out counts as 0. Content lint keeps `level`
 * at or below `combat.levelCap`.
 */
export const equipSchema = z.strictObject({
  /** The item's level, shown to the player to compare it with a dungeon's level. */
  level: int.min(1).max(100),
  stats: z.strictObject({ patk: stat.optional(), pdef: stat.optional(), aatk: stat.optional(), adef: stat.optional() }),
  skills: z.array(ref('skill')).optional(),
});
export type Equip = z.infer<typeof equipSchema>;

export const useSchema = z
  .strictObject({
    /** Where the item can be used. Outside a dungeon the target is implied: a monster for monster_item, a facility for facility_item. */
    contexts: z.array(z.enum(['dungeon', 'airship', 'town'])).min(1),
    /** Grid targeting for dungeon use, same rules as skills (a thrown item is shape line). */
    targeting: targetingSchema.optional(),
    consumed: z.boolean().default(true),
    effects: z.array(effectSchema).min(1),
  })
  .superRefine((u, ctx) => {
    const inDungeon = u.contexts.includes('dungeon');
    if (inDungeon && !u.targeting) ctx.addIssue({ code: 'custom', path: ['targeting'], message: 'dungeon items need targeting' });
    if (!inDungeon && u.targeting) ctx.addIssue({ code: 'custom', path: ['targeting'], message: 'targeting only applies to dungeon use' });
    u.effects.forEach((e, i) => {
      if (e.type === 'damage' && e.style === undefined) {
        ctx.addIssue({ code: 'custom', path: ['effects', i, 'style'], message: 'damage from an item needs style (physical | aetherial)' });
      }
    });
    refuseRevive(u.effects, 'effects', ctx);
  });

/**
 * What a charm does by itself, from the dungeon bag. `when`:
 * party_unit_falls: right after a party unit falls (game-core `defeat`); the
 * first such charm in bag order loses one and its `effects` apply to the
 * fallen unit, in order. Fires mid-round, so it cannot escape and cannot hold
 * a `custom` effect (neither could be refused before the round).
 */
export const autoSchema = z
  .strictObject({
    when: z.enum(['party_unit_falls']),
    effects: z.array(effectSchema).min(1),
  })
  .superRefine((a, ctx) => {
    // Every other effect acts on a unit that is down, so a block without a revive would only use the charm up.
    if (!a.effects.some((e) => e.type === 'revive')) ctx.addIssue({ code: 'custom', path: ['effects'], message: 'an auto block for party_unit_falls needs a revive effect' });
    a.effects.forEach((e, i) => {
      if (e.type === 'damage' && e.style === undefined) {
        ctx.addIssue({ code: 'custom', path: ['effects', i, 'style'], message: 'damage from an item needs style (physical | aetherial)' });
      }
      if (e.type === 'escape' || e.type === 'custom') ctx.addIssue({ code: 'custom', path: ['effects', i], message: `an auto block cannot hold ${e.type}` });
    });
  });
export type ItemAuto = z.infer<typeof autoSchema>;

/**
 * The pixel pattern the dungeon renderer draws an item lying on the floor
 * with (`packages/renderer/src/item-patterns.ts`: the grids; the colours are
 * the theme's `markers.items.palette`). Presentation only, until the item's
 * `icon` asset ships.
 */
export const itemIcons = ['red_potion', 'blue_potion', 'gold_potion', 'green_vial', 'rope', 'orb', 'red_crystal', 'blue_rock', 'sword', 'armor'] as const;
export type ItemIcon = (typeof itemIcons)[number];

export const itemSchema = z
  .strictObject({
    id: ref('item'),
    category: z.enum(itemCategories),
    /** The floor pattern (`itemIcons`); every item has one (integration plan slice 2b). */
    icon: z.enum(itemIcons),
    /** In the game currency (Vals). Towns modify it by percent. */
    basePrice: stat,
    /** Per slot, in the dungeon bag and in airship storage. */
    stackSize: z.strictObject({ dungeon: int.positive(), airship: int.positive() }),
    /**
     * How a player can obtain the item. Omitted: the regular ways, town shops
     * and dungeon floors. A charm lists its ways explicitly.
     */
    sources: z
      .array(z.enum(['shop', 'dungeon', 'craft', 'event']))
      .min(1)
      .refine((a) => new Set(a).size === a.length, 'each source once')
      .optional(),
    /** Required for consumable, monster_item and facility_item; forbidden otherwise. */
    use: useSchema.optional(),
    /** Required for weapon and armor; forbidden otherwise. */
    equip: equipSchema.optional(),
    /** Required for charm; forbidden otherwise. */
    auto: autoSchema.optional(),
    tags: z.array(z.string()).default([]),
  })
  .superRefine((item, ctx) => {
    const usable: readonly string[] = ['consumable', 'monster_item', 'facility_item'];
    if (usable.includes(item.category) && !item.use) {
      ctx.addIssue({ code: 'custom', path: ['use'], message: `${item.category} items need a "use" block` });
    }
    if (!usable.includes(item.category) && item.use) {
      ctx.addIssue({ code: 'custom', path: ['use'], message: `${item.category} items cannot have a "use" block` });
    }
    const equippable = item.category === 'weapon' || item.category === 'armor';
    if (equippable && !item.equip) ctx.addIssue({ code: 'custom', path: ['equip'], message: `${item.category} items need an "equip" block` });
    if (!equippable && item.equip) ctx.addIssue({ code: 'custom', path: ['equip'], message: `${item.category} items cannot have an "equip" block` });
    const charm = item.category === 'charm';
    if (charm && !item.auto) ctx.addIssue({ code: 'custom', path: ['auto'], message: 'charm items need an "auto" block' });
    if (!charm && item.auto) ctx.addIssue({ code: 'custom', path: ['auto'], message: `${item.category} items cannot have an "auto" block` });
    if (!item.equip) return;
    const { stats, skills } = item.equip;
    const allowed: readonly string[] = item.category === 'weapon' ? ['patk', 'aatk'] : ['pdef', 'adef'];
    for (const key of ['patk', 'pdef', 'aatk', 'adef'] as const) {
      if (stats[key] !== undefined && !allowed.includes(key)) {
        ctx.addIssue({ code: 'custom', path: ['equip', 'stats', key], message: `${item.category === 'armor' ? 'an armor' : 'a weapon'} may set only ${allowed.join(' and ')}` });
      }
    }
    if (item.category === 'armor' && skills !== undefined) ctx.addIssue({ code: 'custom', path: ['equip', 'skills'], message: 'armor grants no skills' });
    if (skills !== undefined && new Set(skills).size !== skills.length) {
      ctx.addIssue({ code: 'custom', path: ['equip', 'skills'], message: 'a skill is listed twice' });
    }
  });
export type Item = z.infer<typeof itemSchema>;

// ---------------------------------------------------------------- towns

export const townSchema = z.strictObject({
  id: ref('town'),
  /** Coordinates on the world map, in abstract units. Flight time and fuel derive from distance. */
  position: z.strictObject({ x: int, y: int }),
  /** Price modifiers, as a percent of the item's basePrice. Items not listed trade at 100. */
  market: z
    .array(
      z.strictObject({
        item: ref('item'),
        /** What the town pays when the player sells. */
        sell: percent.default(100),
        /** What the town charges when the player buys. Omit if the town does not stock it. */
        buy: percent.optional(),
      }),
    )
    .default([]),
  tags: z.array(z.string()).default([]),
});
export type Town = z.infer<typeof townSchema>;

// ---------------------------------------------------------------- dungeons

const range = z.strictObject({ min: int.min(0), max: int.min(0) }).refine((r) => r.min <= r.max, 'min must be <= max');

/**
 * Which way a dungeon's floors go (ADR 0032): `down` (a descending dungeon, the default; its floors read B1F, B2F,
 * ...: `ui.floor.down`) or `up` (a tower; 1F, 2F, ...: `ui.floor.up`). Presentation only: game-core never reads it.
 */
export const floorDirections = ['down', 'up'] as const;
export type FloorDirection = (typeof floorDirections)[number];

/**
 * A dungeon configuration: how many floors, how floors are generated, and what
 * lives on them. game-core generates each floor from this plus the server's
 * per-floor seed (ADR 0002). Its look is a `theme` (presentation only).
 */
export const dungeonSchema = z.strictObject({
  id: ref('dungeon'),
  /** World-map coordinates, same units as towns. Flight time and fuel derive from distance. */
  position: z.strictObject({ x: int, y: int }),
  /** Floors per run. The last floor has no exit: defeating its boss ends the run. */
  floors: int.min(1).max(30),
  /** Which way its floors go (`floorDirections`): how a floor is named in the status line, the banners and the log. */
  direction: z.enum(floorDirections).default('down'),
  /** Floor size in tiles, including the outer wall ring. */
  grid: z.strictObject({ width: int.min(12).max(80), height: int.min(12).max(80) }),
  /** Structured generation: rooms placed then joined by corridors. Sizes are interior tiles per side. */
  rooms: z.strictObject({ count: range, size: range }),
  /** Enemy species and levels. `weight` is relative; `level` is at floor 1 and rises by `levelPerFloor` each floor. */
  enemies: z.array(z.strictObject({ monster: ref('monster'), weight: int.positive(), level: range })).min(1),
  enemiesPerFloor: range,
  levelPerFloor: int.min(0).default(0),
  /** Items lying on the floor at generation. `weight` is relative. */
  items: z.array(z.strictObject({ item: ref('item'), weight: int.positive() })).default([]),
  itemsPerFloor: range,
  /**
   * AP springs: tiles that restore `percent` of a party unit's max AP once, when
   * it ends its own turn on one. Each spring's percent is drawn by `weight`.
   */
  springs: z.array(z.strictObject({ percent: int.min(1).max(100), weight: int.positive() })).min(1),
  /** Springs per floor; at least one on every floor. */
  springsPerFloor: range.refine((r) => r.min >= 1, 'a floor has at least one spring (min >= 1)'),
  /**
   * The mini boss of the last floor, placed in the exit room (the last floor has
   * no exit tile). Defeating it gives its species as the run's egg and
   * completes the run at once. `level` is exact (`levelPerFloor` does not apply). Its max HP is
   * `hpPercent`% of the species' HP at that level and every other stat
   * `statPercent`% of the species' stat; both at least 100.
   */
  boss: z.strictObject({ monster: ref('monster'), level: int.min(1).max(100), hpPercent: int.min(100), statPercent: int.min(100) }),
  /** How the renderer dresses its floors (a `theme.<name>`; presentation only, game-core never reads it). */
  theme: ref('theme'),
  tags: z.array(z.string()).default([]),
}).superRefine((d, ctx) => {
  // Floor generation places rooms at random with a one-tile wall between rooms and around the border,
  // and fails the floor if fewer than rooms.count.min fit. These bounds keep that from happening.
  const { width, height } = d.grid;
  const { count, size } = d.rooms;
  if (count.min < 1) ctx.addIssue({ code: 'custom', path: ['rooms', 'count', 'min'], message: 'a floor needs at least one room' });
  if (size.min < 1) ctx.addIssue({ code: 'custom', path: ['rooms', 'size', 'min'], message: 'rooms are at least 1 tile per side' });
  if (size.max + 2 > Math.min(width, height)) {
    ctx.addIssue({ code: 'custom', path: ['rooms', 'size', 'max'], message: `rooms.size.max + 2 must be <= min(grid.width, grid.height) = ${Math.min(width, height)}` });
  }
  // Rough feasibility: count.min rooms of the smallest size, each with its wall margin, must fit the interior.
  if (count.min * (size.min + 1) * (size.min + 1) > (width - 2) * (height - 2)) {
    ctx.addIssue({ code: 'custom', path: ['rooms', 'count', 'min'], message: 'rooms.count.min x (rooms.size.min + 1)^2 must be <= (width - 2) x (height - 2)' });
  }
});
export type Dungeon = z.infer<typeof dungeonSchema>;

// ---------------------------------------------------------------- humans

const humanBaseStatBlock = z.strictObject({ hp: int.positive(), ap: stat, spd: stat });

/**
 * A human party member (Brad). Humans fight like monsters but have no types,
 * no passive and no learnset: `hp`, `ap` and `spd` come from these bases and
 * the human's level, by the same formula as a monster's; `patk`/`aatk` and
 * the skills come from the equipped weapon, `pdef`/`adef` from the armor (item
 * `equip` blocks). Its basic attack is typeless.
 */
export const humanSchema = z.strictObject({
  id: ref('human'),
  /** Base values, fed through the stat formula at the human's level. `hp` is positive. */
  baseStats: humanBaseStatBlock,
  tags: z.array(z.string()).default([]),
});
export type Human = z.infer<typeof humanSchema>;

// ---------------------------------------------------------------- themes

const hexColor = z.string().regex(/^#[0-9a-f]{6}$/, 'expected a colour like "#8c8071" (lower-case #rrggbb)');
/** An integer in [min, max]. */
const within = (min: number, max: number) => int.min(min).max(max);
/** Three integers in [min, max] (a linear RGB multiplier or offset, in percent or per mille). */
const triple = (min: number, max: number) => z.tuple([within(min, max), within(min, max), within(min, max)]);
/** A dark-to-light palette ramp: 4 to 8 colours. */
const ramp = z.array(hexColor).min(4).max(8);
/** Grade-zone offsets in per mille (-500..500 is the prototype's -0.5..0.5): finer than a percent. */
const zoneOffset = within(-500, 500);

/**
 * A light zone of the Medium tier's grade (THEME-KITS "Field" table, `zones`).
 * Units: positions are game tiles (x east, y north); lengths are percent of a
 * tile; offsets are per mille. Exactly one shape: `rect` [x0, y0, x1, y1],
 * the tiles x0..x1 and y0..y1 inclusive (x1 >= x0, y1 >= y0, so one tile is
 * [x, y, x, y]); or `circle` [x, y], a tile centre, with `radiusPercent`.
 */
const lightZoneSchema = z
  .strictObject({
    name: z.string().optional(),
    rect: z.tuple([int, int, int, int]).optional(),
    circle: z.tuple([int, int]).optional(),
    /** A circle's radius, percent of a tile: 1..10000. */
    radiusPercent: within(1, 10000).optional(),
    /** Soft edge, percent of a tile: 1..2000. */
    featherPercent: within(1, 2000),
    tempPermille: zoneOffset,
    lightPermille: zoneOffset,
    airPermille: zoneOffset,
  })
  .superRefine((zone, ctx) => {
    if ((zone.rect === undefined) === (zone.circle === undefined)) ctx.addIssue({ code: 'custom', message: 'a zone has exactly one of rect or circle' });
    if ((zone.circle === undefined) !== (zone.radiusPercent === undefined)) ctx.addIssue({ code: 'custom', message: 'radiusPercent goes with circle, and only with it' });
    if (zone.rect !== undefined && (zone.rect[2] < zone.rect[0] || zone.rect[3] < zone.rect[1])) ctx.addIssue({ code: 'custom', message: 'rect is [x0, y0, x1, y1] inclusive, with x1 >= x0 and y1 >= y0' });
  });

/**
 * A dungeon's look: the colours and the rules the renderer (`@ab/renderer`)
 * uses to dress a floor. Presentation only; game-core never reads it, and it
 * cannot change where anything is or what a tile does. Lifted from the
 * dungeon-floors prototype's theme kit (`data/themes/old-quarry.json` after
 * the HD-2D pass, its ranges in `docs/THEME-KITS.md`). Fractions are integer
 * percents, as everywhere in content (`0.2` is `20`); the few values finer
 * than a percent (grade lift, zone offsets) are integer per mille. Lengths in
 * `...Percent` fields are percent of a tile. The Off tier reads `palette`,
 * `tileMaterials.grainStrengthPercent`, `propRules` and `markers`; the
 * Medium tier (integration plan slice 2a) reads everything. Slice 2b added
 * the gesture and dressing colours, the rail and dressing rules, and the
 * marker detail and item billboard colours.
 */
export const themeSchema = z
  .strictObject({
    id: ref('theme'),
    /** Colours, `#rrggbb`. At Medium the floor, wall, timber and iron colours are also the references of their ramps (albedo = ramp colour × tint / reference). */
    palette: z.strictObject({
      /** Behind everything; also unit noses, blob shadows, the stair pit and the HUD panel. */
      background: hexColor,
      /** Floor slabs and stair sills. */
      floor: hexColor,
      /** Rock walls. */
      wall: hexColor,
      /** What unexplored tiles fade towards at the Off tier (Medium uses `lightRules.fogHue`). */
      fog: hexColor,
      /** Party unit proxies. */
      party: hexColor,
      /** Enemy unit proxies. */
      enemy: hexColor,
      /** The boss proxy and its dais accents. */
      boss: hexColor,
      /** AP spring water. */
      spring: hexColor,
      /** Exit stair treads; also party unit heads, item markers and the HUD text. */
      exit: hexColor,
      /** Wall cap edges, spring rim stones and enemy heads. */
      trim: hexColor,
      /** Timber supports, sleepers and the exit portal. */
      timber: hexColor,
      /** Iron bands, rails and fittings; also item marker caps. */
      iron: hexColor,
      /** The flash of a unit taking damage (a hit, or a burn or poison tick). */
      hit: hexColor,
      /** The flash of a unit being healed. */
      heal: hexColor,
      /** The glow of a unit casting a skill, firing a charge or using an item (slice 2b). */
      cast: hexColor,
      /** The ring of a passive, a status or a stage change (slice 2b). */
      status: hexColor,
      /** Rails and the iron of tools, cart bodies and wheels (THEME-KITS `palette.rail`). */
      rail: hexColor,
      /** Rail ties (THEME-KITS `palette.sleeper`). */
      sleeper: hexColor,
      /** Set dressing: tufts and sprigs, spilled ore, fence rope, crystals (THEME-KITS `palette.foliage`, `ore`, `rope`, `crystal`). */
      foliage: hexColor,
      ore: hexColor,
      rope: hexColor,
      crystal: hexColor,
    }),
    /**
     * Surface detail. The Off tier uses only `grainStrengthPercent` (per-tile
     * colour variation). THEME-KITS "Field" table, "Pixel-art surfaces", "GEO2",
     * "Polish round 4" and "Polish round 5".
     */
    tileMaterials: z.strictObject({
      /** World-space macro patch frequency, percent of 1 per tile: 10..800. */
      detailScalePercent: within(10, 800),
      /** Wall strata wobble frequency, percent of 1 per tile: 10..800. */
      strataScalePercent: within(10, 800),
      /** Off tier: per-tile colour variation, percent: 0..100. */
      grainStrengthPercent: within(0, 100),
      /** Medium tier: per-texel speckle baked into the tone atlas, percent: 0..100 (the prototype's `grainStrength`). */
      surfaceGrainPercent: within(0, 100),
      /** Pixel-art texels per tile: 16..96. */
      texelDensity: within(16, 96),
      /** Ordered-dither strength between adjacent ramp colours, percent: 0..150 (0 is a hard posterize). */
      ditherStrengthPercent: within(0, 150),
      /** Low-frequency level shift in ramp steps, percent of a step: 0..300. */
      macroStrengthPercent: within(0, 300),
      /** Sedimentary bed thickness range, percent of a tile: min 3..100, max 3..150, max >= min. */
      strataBandMinPercent: within(3, 100),
      strataBandMaxPercent: within(3, 150),
      /** Pebble and crack counts in the generated atlas, percent: 0..100. */
      pebbleDensityPercent: within(0, 100),
      crackDensityPercent: within(0, 100),
      /** Dark-to-light palettes (4..8 colours): walking ground, wall faces, rock tops, timber, iron (its lightest entry is the top highlight). */
      ramps: z.strictObject({ ground: ramp, rock: ramp, cap: ramp, timber: ramp, iron: ramp }),
      /** Tone of a rail's top face in the iron ramp, percent: 0..100. */
      railTopTonePercent: within(0, 100),
      /** Share of ground texels recoloured (each of two hues) at unchanged luminance, percent: 0..20. */
      speckDensityPercent: within(0, 20),
      speckWarm: hexColor,
      speckCool: hexColor,
      /** How much pebble density varies in 2-3 tile patches, percent: 0..100. */
      pebbleVariationPercent: within(0, 100),
      /** Rock darkens from `capFadeNearPercent` to `capFadeFarPercent` of a tile (chessboard distance from walkable ground; far > near) down to `capFadeFloorPercent` of its albedo. */
      capFadeNearPercent: within(0, 3000),
      capFadeFarPercent: within(0, 3000),
      capFadeFloorPercent: within(0, 100),
      /** Rock-top atlas texels are this many world texels wide: 1..4. */
      capTexel: within(1, 4),
      /** Share of their texel contrast unexplored surfaces keep, percent: 20..100. */
      fogContrastPercent: within(20, 100),
      /** Warm dirt: hue (luminance from the ground ramp) and share of the ground's macro noise, percent 0..100. */
      groundWarm: hexColor,
      groundWarmSharePercent: within(0, 100),
      /** Moonlit cool rock-top patches: hue and area share, percent 0..100. */
      capCool: hexColor,
      capCoolSharePercent: within(0, 100),
    }),
    /** Where props go. Every placement is seeded by the floor's seed, never by the simulation. */
    propRules: z.strictObject({
      /** A timber frame across a straight corridor every this many tiles: 2..30. */
      supportEvery: within(2, 30),
      /** Frame height, percent of a tile: 80..300. */
      supportHeightPercent: within(80, 300),
      /** Frame the rear wall of every room. */
      roomCornerSupports: z.boolean(),
      /** Chance that a candidate rail route between two rooms is laid, percent: 0..100. */
      railsDensityPercent: within(0, 100),
      /** Chance that a wall tile beside open ground gets a rock shoulder, percent: 0..100. */
      rockDetailDensityPercent: within(0, 100),
      /** Budget of detail boxes (frames, rails, rocks) per floor: 0..1200. Walls, floors and markers are outside it. */
      maxDetailBoxes: within(0, 1200),
      /** Rail tie lattice spacing along straight runs, percent of a tile: 25..80 (THEME-KITS `railTieSpacing`). */
      railTieSpacingPercent: within(25, 80),
      /** Chance of each of up to three grime streaks per track tile, percent: 0..100 (Medium). */
      railGrimePercent: within(0, 100),
      /**
       * Set dressing (THEME-KITS "Dressing", slice 2b). Wall-slot candidate
       * threshold, percent 0..100 (0 disables dressing; also scales fillers and
       * scatter); the floor-wide cap of wall and ledge clusters 0..64; the seed
       * offset added to the floor seed 0..4294967295; the least distance between
       * cluster centres in one room, percent of a tile 80..500.
       */
      dressingDensityPercent: within(0, 100),
      dressingMaxAssemblies: within(0, 64),
      dressingSeedOffset: within(0, 4294967295),
      dressingSpacingPercent: within(80, 500),
      /** Clusters per room floor tile (percent, 0..50) and per room (0..16); ledge clusters per tile of room width (percent, 0..100). */
      dressingPerTilePercent: within(0, 50),
      dressingRoomMax: within(0, 16),
      dressingLedgePerTilePercent: within(0, 100),
      /** Filler chance per free wall slot in rooms and corridors, ground scatter per room cell, terrace-top and foreground cover, percent 0..100. */
      dressingFillerDensityPercent: within(0, 100),
      dressingCorridorFillerPercent: within(0, 100),
      dressingScatterDensityPercent: within(0, 100),
      dressingPlateauDensityPercent: within(0, 100),
      dressingForegroundDensityPercent: within(0, 100),
      /** Floor-wide dressing piece cap: 0..2400. */
      dressingMaxPieces: within(0, 2400),
      /** Kit weights, percent 0..1000. */
      dressingKit: z.strictObject({
        crates: within(0, 1000),
        barrels: within(0, 1000),
        lumber: within(0, 1000),
        tools: within(0, 1000),
        wheelbarrow: within(0, 1000),
        cart: within(0, 1000),
        fence: within(0, 1000),
        rubble: within(0, 1000),
        tufts: within(0, 1000),
        crystals: within(0, 1000),
      }),
    }),
    /**
     * Baked light and fixtures of the Medium tier (static: no dynamic lights,
     * no particles). THEME-KITS "Field" table (`lightRules`), "Light and lens",
     * "Light round 2", "Integration pass", "Frame round 3", "Polish round 4"
     * and "Polish round 5".
     */
    lightRules: z.strictObject({
      /** The fixture siting rule; the only one implemented. */
      torchRule: z.literal('doors-and-turns'),
      /** Fixture light, the static shade floor and the room fill. */
      warm: hexColor,
      shade: hexColor,
      coolFill: hexColor,
      /** Pool radius and ordinary fixture spacing, percent of a tile: 20..2000. */
      poolRadiusPercent: within(20, 2000),
      spacingPercent: within(20, 2000),
      /** Radiance multipliers, percent: 0..800. */
      gainPercent: within(0, 800),
      shadeGainPercent: within(0, 800),
      coolFillGainPercent: within(0, 800),
      /** Route and encounter bounce, percent: 0..300. */
      approachGainPercent: within(0, 300),
      /** Unexplored rock silhouette multiplier, percent: 100..300. */
      unknownRockGainPercent: within(100, 300),
      /** Overlap contribution and ordinary candidate density, percent: 0..100. */
      overlapPercent: within(0, 100),
      torchDensityPercent: within(0, 100),
      /** Dark fixture iron, the flame's hot core and outer tongue, and their gain (percent, 0..800). */
      fixtureColor: hexColor,
      flameCore: hexColor,
      flameColor: hexColor,
      flameGainPercent: within(0, 800),
      /** Grade-zone offsets of the mine shade and of each fixture, per mille: -500..500. */
      zoneShadeTempPermille: zoneOffset,
      zoneShadeLightPermille: zoneOffset,
      zoneWarmTempPermille: zoneOffset,
      zoneWarmLightPermille: zoneOffset,
      /** A torch's hot core: gain percent 0..400, radius percent of a tile 10..300. */
      coreGainPercent: within(0, 400),
      coreRadiusPercent: within(10, 300),
      /** Flame corona brightness (percent, 0..400), radius (percent of a tile, 10..300) and hot-core weight (percent of the gain, 0..400). */
      glowGainPercent: within(0, 400),
      glowRadiusPercent: within(10, 300),
      glowCorePercent: within(0, 400),
      /** Where warm light dominates, cool fills yield by this much, percent: 0..400. */
      coolSuppressPercent: within(0, 400),
      /** Share of the moon and sky light that survives inside the baked volume, percent: 0..100. */
      moonlightPercent: within(0, 100),
      /** The shadow-casting key ("moon") and the hemisphere fill, gains percent 0..400. */
      moon: hexColor,
      moonGainPercent: within(0, 400),
      sky: hexColor,
      skyGround: hexColor,
      skyGainPercent: within(0, 400),
      /** Linear RGB light of unexplored tiles, percent: 0..100 each. */
      unexploredLightPercent: triple(0, 100),
      /** Unexplored surfaces: moon share kept, tint toward `fogHue`, chroma lost, albedo kept (percent, 0..100). */
      unexploredMoonPercent: within(0, 100),
      unexploredTintPercent: within(0, 100),
      unexploredDesaturatePercent: within(0, 100),
      unexploredAlbedoPercent: within(0, 100),
      /** The violet the Medium tier's unexplored surfaces lean toward (the prototype's `palette.fog` after polish 5; Off keeps `palette.fog`). */
      fogHue: hexColor,
      /** Vertical falloff of torch light below and above the fixture, percent of a tile: 20..1000 and 10..1000. */
      torchVerticalRadiusPercent: within(20, 1000),
      torchAboveRadiusPercent: within(10, 1000),
      /** Windowed falloff reach as a multiple of the pool radius, percent: 20..300. */
      falloffScalePercent: within(20, 300),
      /** Hanging lanterns on headers: chance (percent), cap (0..32), pool radius (percent of a tile, 20..500), gain (percent, 0..400), vertical radius (percent of a tile, 20..1000). */
      lanternDensityPercent: within(0, 100),
      lanternMax: within(0, 32),
      lanternRadiusPercent: within(20, 500),
      lanternGainPercent: within(0, 400),
      lanternVerticalRadiusPercent: within(20, 1000),
      /** Contact darkening of floor near walls, percent: 0..100. */
      wallOcclusionPercent: within(0, 100),
      /** Moon share kept by rock deep in the mass, percent: 0..100. */
      rockMoonPercent: within(0, 100),
      /** Cool pool over every room: colour, gain (percent, 0..400), size (percent of the room's half extents, 20..300), moon share outside the pools (percent, 0..100). */
      moonPool: hexColor,
      moonPoolGainPercent: within(0, 400),
      moonPoolScalePercent: within(20, 300),
      moonPoolEdgePercent: within(0, 100),
      /** Cap on warm fixture light on walls and props (bake units, percent: 0..800). */
      wallTorchMaxPercent: within(0, 800),
      /** Rock within this many tiles of open ground keeps the room's moon share, percent of a tile: 0..400. */
      ledgeMoonPercent: within(0, 400),
      /** Floor braziers keep this distance from rail centre lines, percent of a tile: 0..300. */
      torchRailClearancePercent: within(0, 300),
      /** Every room entrance or turn farther than this from a fixture takes a floor brazier, percent of a tile: 0..2000 (0: off). */
      brazierReachPercent: within(0, 2000),
      /** A remaining sconce's light sits this far out from its wall, percent of a tile: 0..100. */
      sconceReachPercent: within(0, 100),
      /** Rock-top ambient floor: colour, gain (percent, 0..200), where it starts to fall and where it reaches its far share (percent of a tile; reach > near), far share, share on steep faces (percent, 0..100). */
      capAmbient: hexColor,
      capAmbientGainPercent: within(0, 200),
      capAmbientNearPercent: within(0, 2000),
      capAmbientReachPercent: within(0, 2000),
      capAmbientFarPercent: within(0, 100),
      capAmbientSidePercent: within(0, 100),
      /** Cap on warm light on floor-level rock (bake units, percent 0..800), fading over this many tiles into the rock (percent of a tile, 5..500). */
      capTorchMaxPercent: within(0, 800),
      capTorchFadePercent: within(5, 500),
    }),
    /** Extra light zones for the grade (THEME-KITS "Field" table, `zones`); the fixtures and the mine shade add their own. */
    zones: z.array(lightZoneSchema),
    /** The Medium tier's one merged lens pass (THEME-KITS "Field" table, "Light and lens", "Frame round 3", "Polish round 4", "Polish round 5"). */
    grade: z.strictObject({
      /** Shadow, highlight and gain multipliers, linear RGB percent: 0..400. */
      shadowPercent: triple(0, 400),
      highlightPercent: triple(0, 400),
      gainPercent: triple(0, 400),
      /** Linear RGB shadow floor, per mille: 0..50. */
      liftPermille: triple(0, 50),
      /** Saturation 0..200, contrast 10..200, vignette 0..100, zone strength 0..100 (percent). */
      saturationPercent: within(0, 200),
      contrastPercent: within(10, 200),
      vignettePercent: within(0, 100),
      zoneStrengthPercent: within(0, 100),
      /** Warmth near fixtures (percent, 0..100) and its radius (percent of a tile, 20..1000). */
      lampWarmthPercent: within(0, 100),
      lampRadiusPercent: within(20, 1000),
      /** Film grain (0..20) and dust motes (0..30), percent. */
      grainPercent: within(0, 20),
      dustPercent: within(0, 30),
      /** Tilt-shift strength, and its scale above and below the leader band, percent: 0..150. */
      tiltPercent: within(0, 150),
      tiltTopPercent: within(0, 150),
      tiltBottomPercent: within(0, 150),
      /** Screen-height offsets below the leader band where the near-field blur starts and is full, percent: 0..100 (end > start). */
      tiltBottomStartPercent: within(0, 100),
      tiltBottomEndPercent: within(0, 100),
      /** Edge haze: the sRGB a black pixel shows at the top and bottom edge, the clear half-height about the centre (percent, 0..49) and the edge weight (percent, 0..60). */
      hazeTop: hexColor,
      hazeBottom: hexColor,
      hazeClearPercent: within(0, 49),
      hazeMaxPercent: within(0, 60),
    }),
    /** Light shafts from rock cracks into large rooms (THEME-KITS "Field" table, `shafts`). */
    shafts: z.strictObject({
      /** Rooms of at least this many tiles (1..4096) get one, up to `maxPerFloor` (0..12). */
      minRoomArea: within(1, 4096),
      maxPerFloor: within(0, 12),
      /** Width (10..500) and advisory length (10..800), percent of a tile; strength percent 0..300. */
      widthPercent: within(10, 500),
      lengthPercent: within(10, 800),
      strengthPercent: within(0, 300),
      /** Colour, landing pool radius (percent of a tile, 10..1000) and gain (percent, 0..300). */
      color: hexColor,
      landingRadiusPercent: within(10, 1000),
      landingGainPercent: within(0, 300),
    }),
    /** Seeded dust and rubble decals on floor tiles (THEME-KITS "Field" table, `decals`): chance, tint, attenuation (percent, 0..100). */
    decals: z.strictObject({
      densityPercent: within(0, 100),
      color: hexColor,
      strengthPercent: within(0, 100),
    }),
    /** The boss room (THEME-KITS "Field" table, `bossRoom`): whether the dais carries its own light, and the accent of its lights and fixtures. */
    bossRoom: z.strictObject({
      dais: z.boolean(),
      accent: hexColor,
    }),
    markers: z.strictObject({
      exit: z.strictObject({
        /** Stair treads: 5..10. */
        steps: within(5, 10),
        /** How far the stair descends, percent of a tile: 14..34. */
        depthPercent: within(14, 34),
        /** The exit lantern's light (Medium) and its glass. */
        light: hexColor,
        /** The exit portal's timber and iron, and the boss room's frame and braziers (slice 2b). */
        timber: hexColor,
        iron: hexColor,
      }),
      spring: z.strictObject({
        /** The spring's light (Medium). */
        water: hexColor,
        /** Basin stone, rim stones and the ripple of the water (slice 2b). */
        stone: hexColor,
        rim: hexColor,
        ripple: hexColor,
      }),
      /**
       * Item billboards (slice 2b): colours by pixel symbol (THEME-KITS
       * `markers.items.palette`). k outline, c cork, g glass, w highlight,
       * s shadow, h crystal highlight, p crystal face, d dark face; liquids and
       * bodies r red, b blue, y gold, v green, t rope, o orb, n dark blue,
       * x steel, e egg shell, m egg spots.
       */
      items: z.strictObject({
        palette: z.strictObject({
          k: hexColor,
          c: hexColor,
          g: hexColor,
          w: hexColor,
          s: hexColor,
          h: hexColor,
          p: hexColor,
          d: hexColor,
          r: hexColor,
          b: hexColor,
          y: hexColor,
          v: hexColor,
          t: hexColor,
          o: hexColor,
          n: hexColor,
          x: hexColor,
          e: hexColor,
          m: hexColor,
        }),
      }),
    }),
    tags: z.array(z.string()).default([]),
  })
  .superRefine((t, ctx) => {
    const order = (path: string[], low: number, high: number, message: string): void => {
      if (high <= low) ctx.addIssue({ code: 'custom', path, message });
    };
    const m = t.tileMaterials;
    if (m.strataBandMaxPercent < m.strataBandMinPercent) ctx.addIssue({ code: 'custom', path: ['tileMaterials', 'strataBandMaxPercent'], message: 'must be >= strataBandMinPercent' });
    order(['tileMaterials', 'capFadeFarPercent'], m.capFadeNearPercent, m.capFadeFarPercent, 'must exceed capFadeNearPercent');
    order(['lightRules', 'capAmbientReachPercent'], t.lightRules.capAmbientNearPercent, t.lightRules.capAmbientReachPercent, 'must exceed capAmbientNearPercent');
    order(['grade', 'tiltBottomEndPercent'], t.grade.tiltBottomStartPercent, t.grade.tiltBottomEndPercent, 'must exceed tiltBottomStartPercent');
  });
export type Theme = z.infer<typeof themeSchema>;

// ---------------------------------------------------------------- scenes

/**
 * The prefabs a scene may place: the renderer's catalog (`@ab/renderer`,
 * `scene/prefabs/<id>.ts`, each a `buildPrefab(ctx, params)`), listed here so
 * content lint can check a placement without importing the renderer. The
 * renderer asserts in a vitest that its catalog is exactly this list (ADR
 * 0031). Lifted from the lantern-water prototype: every piece its
 * `layout.js`, `world.js` and the prefab modules place (a piece `world.js`
 * builds once is a prefab with one placement).
 */
export const scenePrefabIds = [
  // The town (lantern-water `layout.js` PREFABS and `world.js` createTown).
  'house_apothecary',
  'house_meridian',
  'house_west',
  'house_river',
  'house_east',
  'house_market',
  'bridge',
  'street_tree',
  'cargo',
  'quay_lamp',
  'bench',
  'bell_tower',
  'fountain_court',
  'market_stall',
  'market_cart',
  'fishing_rod',
  'shrub_scatter',
  'leaf_litter',
  'street_bunting',
  'river_reeds',
  'skiff',
  'crate_stack',
  'distant_hills',
  'river_banks',
  'perimeter_rail',
  'mooring_finger',
  'boat_landing',
  'quay_fittings',
  'backdrop_town',
  'backdrop_south',
  'backdrop_west',
  'northern_crossing',
  'eastern_garden',
  // ADR 0032: the airship's mooring at the boat landing (a bollard, a rope, a gangway's end; not in the prototype).
  'airship_mooring',
  // The apothecary (lantern-water `world.js` createShop).
  'shop_room',
  'shop_shelf',
  'shop_counter',
  'shop_wall_decor',
  'drying_herbs',
  'shop_rug',
  'worktable',
  'hearth',
  'chandelier',
  'barrel',
  'crate',
  'planter',
  'exit_mat',
  // The airship (slice 7a, ADR 0033; lifted from brad-airship). A world-coordinate set piece: the hull, the rig,
  // the deck, the mooring island, the jetty and the gangplank (`airship-exterior.js`, `airship-world.js`).
  'airship_exterior',
  // Pieces placed by the deck's placements: the jetty's mooring post and the netted cargo on deck (`ship.json` props),
  // and a painted clutter card or decal (`airship-clutter.js`; every one of a scene is drawn as one mesh).
  'ship_mooring_post',
  'ship_cargo',
  'ship_clutter',
  // The rooms (`room-types.json` levels): placed by a ship's room grid (`ship.rooms`), never by `placements`.
  'ship_cockpit_1',
  'ship_cockpit_2',
  'ship_hatchery_1',
  'ship_hatchery_2',
  'ship_farm_1',
  'ship_farm_2',
] as const;
export type ScenePrefabId = (typeof scenePrefabIds)[number];

/** The prefabs only a ship scene may place (ADR 0033): the ship's own pieces, drawn with the ship's materials. */
export const shipPrefabIds: readonly ScenePrefabId[] = ['airship_exterior', 'ship_mooring_post', 'ship_cargo', 'ship_clutter', 'ship_cockpit_1', 'ship_cockpit_2', 'ship_hatchery_1', 'ship_hatchery_2', 'ship_farm_1', 'ship_farm_2'];

/** The room prefabs: a room type's level names one (`rules/ship_rooms.yaml`); the room grid places them, a placement never does. */
export const shipRoomPrefabIds: readonly ScenePrefabId[] = ['ship_cockpit_1', 'ship_cockpit_2', 'ship_hatchery_1', 'ship_hatchery_2', 'ship_farm_1', 'ship_farm_2'];

/** Scene lengths are metres (ADR 0031: decimals allowed, at most three places; the one exception to the integer rule). */
const thousandths = (v: number): boolean => Math.abs(v * 1000 - Math.round(v * 1000)) < 1e-6;
const metres = z.number().refine(thousandths, 'metres with at most three decimals');
/** A positive scene number with at most three decimals (an intensity, a speed, a scale). */
const amount = z.number().nonnegative().refine(thousandths, 'at most three decimals');
const sceneId = /^[a-z0-9_]+$/;
const scenePoint = z.strictObject({ x: metres, y: metres });
const scenePoint3 = z.strictObject({ x: metres, y: metres, h: metres });
/** An axis-aligned rectangle on the ground: x0..x1 east, y0..y1 north. */
const sceneRect = z
  .strictObject({ x0: metres, y0: metres, x1: metres, y1: metres })
  .refine((r) => r.x1 > r.x0 && r.y1 > r.y0, 'a rect needs x1 > x0 and y1 > y0');
const facing = z.enum(['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw']);

/**
 * A walkable surface (the higuera prototype's vocabulary `{id, x0, x1, z0, z1,
 * y, polygon}` in game axes): a rectangle, a circle or a polygon at height
 * `h`. `arch` adds a bridge's profile along x (lantern-water `world.js`
 * `bridge`: from `left` at `from` to `right` at `to`, plus `lift` and
 * `rise × sin(πt)`); `steps` a flight down along x. A point on several surfaces stands on the highest, unless
 * one is `exclusive` (the boat landing's steps below the quay).
 */
const surfaceSchema = z
  .strictObject({
    id: z.string().regex(sceneId),
    x0: metres.optional(),
    y0: metres.optional(),
    x1: metres.optional(),
    y1: metres.optional(),
    circle: z.strictObject({ x: metres, y: metres, r: metres }).optional(),
    polygon: z.array(z.tuple([metres, metres])).min(3).optional(),
    h: metres,
    arch: z.strictObject({ from: metres, to: metres, left: metres, right: metres, rise: metres, lift: metres }).optional(),
    /** Steps down along x (the boat landing, `boat-landing.js`): from `from`, every `width` the height drops by `drop`, at most `count - 1` times. */
    steps: z.strictObject({ from: metres, width: amount, drop: amount, count: int.positive() }).optional(),
    exclusive: z.boolean().optional(),
  })
  .superRefine((s, ctx) => {
    const rect = s.x0 !== undefined && s.y0 !== undefined && s.x1 !== undefined && s.y1 !== undefined;
    const any = s.x0 !== undefined || s.y0 !== undefined || s.x1 !== undefined || s.y1 !== undefined;
    const shapes = [rect, s.circle !== undefined, s.polygon !== undefined].filter(Boolean).length;
    if (shapes !== 1 || (any && !rect)) ctx.addIssue({ code: 'custom', message: 'a surface has exactly one shape: x0/y0/x1/y1, circle or polygon' });
    if (rect && ((s.x1 ?? 0) <= (s.x0 ?? 0) || (s.y1 ?? 0) <= (s.y0 ?? 0))) ctx.addIssue({ code: 'custom', message: 'a rect needs x1 > x0 and y1 > y0' });
  });

/** A piece of the renderer's catalog at a place. `rotation` is degrees counter-clockwise seen from above (east toward north). */
const placementSchema = z.strictObject({
  id: z.string().regex(sceneId),
  prefab: z.enum(scenePrefabIds),
  x: metres,
  y: metres,
  h: metres.optional(),
  rotation: z.number().refine(thousandths).optional(),
  params: z.record(z.string(), z.union([z.number(), z.boolean(), z.string()])).optional(),
});

const lightSchema = z.strictObject({
  id: z.string().regex(sceneId),
  kind: z.enum(['point', 'spot']),
  x: metres,
  y: metres,
  h: metres,
  color: hexColor,
  /** Intensity by day and by night (three.js physical units, as the prototype set them). */
  intensity: amount,
  nightIntensity: amount,
  distance: amount,
  decay: amount,
  /** Whether it casts a shadow map (Medium only; Off draws no point or spot light). */
  shadow: z.boolean(),
  shadowMapSize: int.positive().optional(),
  /** A spot light's aim, cone half-angle (radians) and penumbra. */
  target: scenePoint3.optional(),
  angle: amount.optional(),
  penumbra: amount.optional(),
  /** A spot light's gobo: the leaded window of the apothecary (`window-lighting.js`). */
  gobo: z.enum(['leaded_window']).optional(),
  /**
   * Flicker: the intensity times `1 + Σ amount × sin(time × speed + phase)` over the waves (the prototype's
   * `main.js` `updateLighting`: the town's lanterns one wave at 2.1 with the phase `index × 1.7`; the shop's
   * hearth and chandelier two, at 3.7 and 7.1, 0.018 each, phases `index × 2.1` and `index`), scaled by the
   * motion strength (none under `prefers-reduced-motion`).
   */
  flicker: z.array(z.strictObject({ speed: amount, amount, phase: z.number().refine(thousandths) })).optional(),
});

/**
 * A flock of birds (lantern-water's `engine/src/birds.js` `LANTERNWATER_FLOCKS`): `count` birds scattered
 * around (x, y) within `spread` metres on the ground (`h` omitted: the walkable height there), or perched in a
 * line `along` a direction (`perch`), or on explicit `spots` (perched). They scatter when the traveler (or a
 * walking resident) comes within `radius` and return after a quiet spell. Drawn at Medium only.
 */
const flockSchema = z
  .strictObject({
    id: z.string().regex(/^[a-z0-9_]+$/),
    species: z.enum(['pigeon', 'gull']),
    x: metres.optional(),
    y: metres.optional(),
    h: metres.optional(),
    spread: amount.optional(),
    count: int.positive().optional(),
    perch: z.boolean().optional(),
    along: z.strictObject({ x: metres, y: metres }).optional(),
    spots: z.array(z.strictObject({ x: metres, y: metres, h: metres })).min(1).optional(),
    radius: amount,
  })
  .superRefine((f, ctx) => {
    const placed = f.spots !== undefined || (f.x !== undefined && f.y !== undefined && f.spread !== undefined && f.count !== undefined);
    if (!placed) ctx.addIssue({ code: 'custom', message: 'a flock has spots, or x, y, spread and count' });
    if (f.spots === undefined && f.perch === true && f.along === undefined) ctx.addIssue({ code: 'custom', message: 'a perched line needs along' });
  });

/**
 * The GPU point systems: lantern-water's three (`ambient-motion.js`; their paths stay in the shader) and the
 * airship's drifting motes (`airship-weather.js` `createMotes`, ADR 0033: their colour, alpha and radius are the time
 * of day's `motes`).
 */
const emitterSchema = z.strictObject({
  kind: z.enum(['dust', 'smoke', 'leaves', 'motes']),
  count: int.positive(),
  /** Dust and motes: the box they wrap in (x0..x1, y0..y1, from `base` up to `height`); leaves: where they fall around. */
  area: sceneRect.optional(),
  height: metres.optional(),
  base: metres.optional(),
  /** Smoke: the chimney tops. */
  sources: z.array(scenePoint3).optional(),
  /** Point size in the vertex shader: pixels per metre at the near plane, and its cap (the town's; the motes size by the camera). */
  size: amount.optional(),
  maxSize: amount.optional(),
});

/**
 * How the camera's target follows the traveler (`main.js`): x by `x` (`xNarrow` below 600 px wide), h by `h` over
 * `base`, y by `y` then `yOffset`, each measured from `anchor` (default the origin; the airship's rooms anchor on
 * the room's centre and follow by 0.07: `airshipCameraFrame`).
 */
const cameraFollowSchema = z.strictObject({ x: amount, xNarrow: amount, h: amount, base: metres, y: amount, yOffset: metres, anchor: scenePoint.optional() });

const cameraPresetSchema = z.strictObject({
  /** The camera's offset from its target: east, north (negative: south of it) and up. */
  offset: scenePoint3,
  /** The view's vertical span at the target, metres; portrait screens widen it by `portraitSpan`. */
  span: amount,
  portraitSpan: amount,
  follow: cameraFollowSchema,
  /** The target's easing rate per second (exp smoothing); 0 snaps. */
  damping: amount,
  /** The span is at least this width over the aspect (the airship's rooms: the room's width and a margin fill the frame). */
  fitWidth: amount.optional(),
  /** Below this aspect (a portrait screen) another offset, span and follow (the airship's `airshipCameraFrame`). */
  portrait: z.strictObject({ below: amount, offset: scenePoint3, span: amount, follow: cameraFollowSchema }).optional(),
  /** Frame the whole built place instead of the traveler (the airship's overview: its projected bounds fill the frame). */
  fit: z.boolean().optional(),
});

const gradePresetSchema = z.strictObject({
  shadow: z.tuple([amount, amount, amount]),
  highlight: z.tuple([amount, amount, amount]),
  lift: z.tuple([amount, amount, amount]),
  gain: z.tuple([amount, amount, amount]),
  contrast: amount,
  saturation: amount,
  vignette: z.tuple([amount, amount, amount]),
  haze: z.tuple([amount, amount, amount]),
  hazeAmount: amount,
  zone: amount,
  glare: amount,
});

const zoneSchema = z.strictObject({
  name: z.string(),
  rect: sceneRect.optional(),
  circle: z.strictObject({ x: metres, y: metres, r: metres }).optional(),
  feather: amount,
  temp: z.number().refine(thousandths),
  light: z.number().refine(thousandths),
  air: z.number().refine(thousandths).optional(),
});

/** A colour, or a colour times a gain (the airship's sky: `["#ffb088", 1.05]` in `airship-time.js`). */
const skyColor = z.union([hexColor, z.strictObject({ color: hexColor, gain: amount })]);

/**
 * The airship's procedural sky and cloud sea (`airship-sky.js`), per time of day (`airship-time.js` `RAW[*].sky`):
 * where the sun and the moon stand (degrees: azimuth from north toward the bow, elevation), the sun disc's size and
 * power, the moon and stars, the layers' colours (sky, sun, the cloud sea, the towering cumulus, the mountain range,
 * the floating islands, the mist and the high cirrus) and their amounts.
 */
const shipSkySchema = z.strictObject({
  sunAt: z.strictObject({ azimuth: z.number().refine(thousandths), elevation: z.number().refine(thousandths) }),
  moonAt: z.strictObject({ azimuth: z.number().refine(thousandths), elevation: z.number().refine(thousandths) }),
  sunSize: amount,
  sunPower: amount,
  moon: amount,
  stars: amount,
  seaCover: amount,
  towerHeight: amount,
  towerCover: amount,
  mist: amount,
  cirrus: amount,
  rangeHaze: amount,
  isleHaze: amount,
  rays: amount,
  colors: z.strictObject({
    zenith: skyColor,
    horizon: skyColor,
    sunGlow: skyColor,
    sun: skyColor,
    moon: skyColor,
    seaLit: skyColor,
    seaShade: skyColor,
    seaDeep: skyColor,
    seaFar: skyColor,
    towerLit: skyColor,
    towerShade: skyColor,
    rim: skyColor,
    range: skyColor,
    isle: skyColor,
    isleLit: skyColor,
    mist: skyColor,
    cirrus: skyColor,
  }),
});

/**
 * One time of day (ADR 0033: the town's day and night and the airship's five times are presets of one shape). The
 * renderer blends from the preset shown to the next over `lighting.blend` seconds. `lamps` (0 by day, 1 at night)
 * scales everything lamp-lit: the lights' night intensity, the lantern glass and windows, the canal's night, the
 * airship's `nightGlow` materials and baked lamp light. `background` is the clear colour and the fog's.
 */
const lightingPresetSchema = z.strictObject({
  lamps: amount.refine((v) => v <= 1, 'lamps is 0 (day) to 1 (night)'),
  exposure: amount,
  background: hexColor,
  /** The exponential fog's density × 10⁴ (an integer: 45 is lantern-water's 0.0045; ADR 0031). */
  fogDensityE4: int.nonnegative(),
  hemisphere: z.strictObject({ sky: hexColor, ground: hexColor, intensity: amount }),
  /** The sun's colour and intensity; `direction` (toward the light, game axes) when the time of day moves it (the airship). */
  sun: z.strictObject({ color: hexColor, intensity: amount, direction: scenePoint3.optional() }),
  /** The fill light's intensity (a town's: `lighting.fill`). */
  fill: amount.optional(),
  /** Lantern glass and the windows' glow (a town's emissive intensities). */
  glow: z.strictObject({ glass: amount, windows: amount, coolWindows: amount }).optional(),
  bloom: amount,
  grade: gradePresetSchema,
  /** The airship's sky (`airship-sky.js`). */
  sky: shipSkySchema.optional(),
  /** The airship's wind and cloud shadows (`airship-time.js`: `wind`, `windDir` on the ground, `cloudShadow`). */
  weather: z.strictObject({ wind: amount, direction: scenePoint, cloudShadow: amount }).optional(),
  /** The far layer in the lens (the airship's sky): its tint, mix and blur (`farTint`, `farMix`, `farBlur`). */
  far: z.strictObject({ tint: hexColor, mix: amount, blur: amount }).optional(),
  /** The portlights' daylight below deck (the airship's rooms: `airship-time.js` `portlight`). */
  portlight: z.strictObject({ color: hexColor, intensity: amount }).optional(),
  /** How much smoke the galley stove sends up (`airship-flight.js` `SMOKE_BY_TIME`). */
  smoke: amount.optional(),
  /** Dew on the deck's planking (the airship's dawn: a wet sheen). */
  dew: amount.optional(),
  /** The drifting motes' colour, alpha and radius in metres (the airship's `motes` emitter). */
  motes: z.strictObject({ color: hexColor, alpha: amount, radius: amount }).optional(),
});

/**
 * A ship's room grid (ADR 0033, lifted from brad-airship `data/ship.json` and `docs/SHIP-DATA-MODEL.md`). Each deck
 * has an origin (the grid's north-west corner: columns run east, rows south) and a height; a room takes the slot of
 * `size` cells whose north-west cell is `cell`, turned `rotation` degrees clockwise (seen from above) about the
 * slot's centre; its doors are on its unturned walls, `offset` cells from the wall's west (north and south walls) or
 * north (east and west walls) end. Its type and level are `rules/ship_rooms.yaml`'s. The schema checks every room's
 * turned extent lies on its deck's grid and that no two rooms of a deck overlap (`shipRoomExtent`); content lint
 * checks the types, levels, sizes and doors against the rules.
 *
 * A door with `to` is a passage to another scene (`data/ship.json`'s portal links): the room's scene gets a portal
 * just inside it, leading to `to.scene`'s spawn `to.spawn`, and a spawn of the door's id (`<room>_<side>`) a step
 * further in, where a traveler coming the other way arrives (`shipPassages`). A door that meets a door of another
 * room on the same deck is a doorway between the two rooms; any other passage is a hatch to another deck (the
 * companionway: a trapdoor is drawn at it). The passages' insets and radii are the room type's (`passages`).
 */
export const shipSides = ['north', 'east', 'south', 'west'] as const;
export type ShipSide = (typeof shipSides)[number];
const shipRoomSchema = z.strictObject({
  id: z.string().regex(sceneId),
  type: z.string().regex(sceneId),
  deck: z.string().regex(sceneId),
  cell: z.tuple([int.nonnegative(), int.nonnegative()]),
  size: z.tuple([int.positive(), int.positive()]),
  rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]),
  level: int.positive(),
  doors: z.array(z.strictObject({ side: z.enum(shipSides), offset: int.nonnegative(), to: z.strictObject({ scene: ref('scene'), spawn: z.string().regex(sceneId) }).optional() })),
});
export type ShipRoom = z.infer<typeof shipRoomSchema>;
const shipGridSchema = z.strictObject({
  cell: int.positive(),
  cols: int.positive(),
  rows: int.positive(),
  decks: z.array(z.strictObject({ id: z.string().regex(sceneId), origin: scenePoint, h: metres })).min(1),
});
export type ShipGrid = z.infer<typeof shipGridSchema>;

/** A room's turned extent in game axes (x east, y north): its centre, and its width (along x) and depth (along y) once turned. */
export type ShipRoomExtent = { readonly x0: number; readonly y0: number; readonly x1: number; readonly y1: number; readonly cx: number; readonly cy: number; readonly h: number };

/**
 * Where a room lies (the one formula, used by the schema and by `@ab/scene`): the slot's centre is the deck's origin
 * plus (cell + size / 2) cells east and south; a quarter turn swaps the room's width and depth about that centre.
 * Null when its deck is not on the grid.
 */
export const shipRoomExtent = (grid: ShipGrid, room: ShipRoom): ShipRoomExtent | null => {
  const deck = grid.decks.find((d) => d.id === room.deck);
  if (deck === undefined) return null;
  const cx = deck.origin.x + (room.cell[0] + room.size[0] / 2) * grid.cell;
  const cy = deck.origin.y - (room.cell[1] + room.size[1] / 2) * grid.cell;
  const quarter = (room.rotation / 90) % 2 === 1;
  const hx = ((quarter ? room.size[1] : room.size[0]) * grid.cell) / 2;
  const hy = ((quarter ? room.size[0] : room.size[1]) * grid.cell) / 2;
  return { x0: cx - hx, y0: cy - hy, x1: cx + hx, y1: cy + hy, cx, cy, h: deck.h };
};

const shipSchema = z
  .strictObject({ grid: shipGridSchema, rooms: z.array(shipRoomSchema) })
  .superRefine((ship, ctx) => {
    const g = ship.grid;
    const seen = new Set<string>();
    const placed: { readonly room: ShipRoom; readonly e: ShipRoomExtent }[] = [];
    ship.rooms.forEach((room, i) => {
      if (seen.has(room.id)) ctx.addIssue({ code: 'custom', path: ['rooms', i, 'id'], message: `room "${room.id}" is listed twice` });
      seen.add(room.id);
      const e = shipRoomExtent(g, room);
      const deck = g.decks.find((d) => d.id === room.deck);
      if (e === null || deck === undefined) {
        ctx.addIssue({ code: 'custom', path: ['rooms', i, 'deck'], message: `deck "${room.deck}" is not on the grid` });
        return;
      }
      const eps = 1e-6;
      const right = deck.origin.x + g.cols * g.cell;
      const bottom = deck.origin.y - g.rows * g.cell;
      if (e.x0 < deck.origin.x - eps || e.x1 > right + eps || e.y1 > deck.origin.y + eps || e.y0 < bottom - eps) {
        ctx.addIssue({ code: 'custom', path: ['rooms', i], message: `room "${room.id}" turned ${room.rotation}° reaches off its deck's grid` });
      }
      for (const other of placed) {
        if (other.room.deck !== room.deck) continue;
        const o = other.e;
        if (e.x0 < o.x1 - eps && e.x1 > o.x0 + eps && e.y0 < o.y1 - eps && e.y1 > o.y0 + eps) ctx.addIssue({ code: 'custom', path: ['rooms', i], message: `rooms "${other.room.id}" and "${room.id}" overlap` });
      }
      placed.push({ room, e });
      room.doors.forEach((d, j) => {
        const along = d.side === 'north' || d.side === 'south' ? room.size[0] : room.size[1];
        if (d.offset >= along) ctx.addIssue({ code: 'custom', path: ['rooms', i, 'doors', j], message: `a ${d.side} door's offset ${d.offset} is past the wall's ${along} cells` });
      });
    });
  });
export type Ship = z.infer<typeof shipSchema>;

/** A solid footprint: the walker keeps `walk.pad` away. A ship names its fixtures (`id`), rails and their height (the art reads them). */
const colliderSchema = z
  .strictObject({ x0: metres, y0: metres, x1: metres, y1: metres, id: z.string().regex(sceneId).optional(), kind: z.enum(['rail', 'fixture']).optional(), height: amount.optional() })
  .refine((r) => r.x1 > r.x0 && r.y1 > r.y0, 'a rect needs x1 > x0 and y1 > y0');

/**
 * What an interactable does (slices 6b and 7a). `talk`, `read`, `wish`, `door`, `shop` and `board` as before (a board,
 * ADR 0033: boards the airship through the portal of its id); `fly` (the airship's wheel: the destinations of
 * `rules/routes.yaml`); `incubator` (a hatchery's, numbered by `index`; ADR 0033); `hatch_spot` (where a hatchling is
 * set down; no action); `plot`, `bench`, `rack` (the farm's: no action until the farm loop, post-MVP); `fixture` (a
 * room's furnishing the room's prefab draws, named by `fixture`: no action).
 */
/** The furnishings a room's prefab draws at a `fixture` interactable (ADR 0033). */
export const shipFixtures = ['console', 'seat', 'chart_table', 'flag_locker', 'watering_can'] as const;
/** What a crew member does at a post (`airship-crew.js` `ACTIONS`): the poses the sprites repo is asked for. */
export const crewActions = ['read', 'coil', 'sew', 'watch', 'helm'] as const;
export type CrewAction = (typeof crewActions)[number];

export const interactableKinds = ['talk', 'read', 'wish', 'door', 'shop', 'board', 'fly', 'incubator', 'hatch_spot', 'plot', 'bench', 'rack', 'fixture'] as const;
export type InteractableKind = (typeof interactableKinds)[number];

/**
 * A walkable place: a town or an interior (ADR 0031). Game axes: x east, y
 * north on the ground, `h` the elevation; lengths in metres (`units`). The
 * renderer maps (x, y, h) to three.js (x, h, -y). Interiors share their
 * town's `place`. `@ab/scene` walks it (surfaces, colliders, channels,
 * portals); `@ab/renderer` draws it (placements, terrain, lights, emitters,
 * water, camera, lighting, grade, atmosphere). Lifted from the lantern-water
 * prototype by a one-off script (ADR 0031).
 */
export const sceneSchema = z.strictObject({
  id: ref('scene'),
  /** The town it belongs to (an airship's: the town it is moored at for now; which town is a later refinement, ADR 0033). */
  place: ref('town'),
  /** `ship_deck` (the airship's deck, the scene that holds its room grid) and `ship_room` (a room below deck): ADR 0033. */
  kind: z.enum(['town', 'interior', 'ship_deck', 'ship_room']),
  units: z.literal('metres'),
  /** A ship's room grid (the `ship_deck` scene's): `shipSchema`. */
  ship: shipSchema.optional(),
  /**
   * The mooring views a ship's deck can show beyond its rail (Q53, ADR 0034): named backdrops. `generic` is the
   * prototype's cliff, jetty and island (drawn by the deck's `airship_exterior`); a town may get its own later (new
   * art, a new name here). Required on a `ship_deck` (with `generic`), refused elsewhere (content lint).
   */
  mooringViews: z.array(z.string().regex(sceneId)).min(1).optional(),
  /**
   * A town's mooring view (Q53, ADR 0034): which of the deck's `mooringViews` shows while the ship is moored there;
   * absent, `generic`. Recorded only: the deck draws `generic` until a second view exists.
   */
  mooringView: z.string().regex(sceneId).optional(),
  /** A ship scene's place on its ship: the scene holding the grid (`ship`), the deck it shows, and its rooms on that deck (all of them when absent). */
  onShip: z.strictObject({ ship: ref('scene'), deck: z.string().regex(sceneId), rooms: z.array(z.string().regex(sceneId)).optional() }).optional(),
  /** Where the walking model may go (navigation bounds; the prototype's `bounds`). */
  bounds: sceneRect,
  /** The terrain the renderer builds (a town): quay districts, raised terraces with their stairs, the canal. */
  terrain: z
    .strictObject({
      /** The whole ground the scene's surfaces must lie within. */
      extent: sceneRect,
      /** Raised districts: a rect and its height (`terrain.js` `districtHeight`). */
      districts: z.array(z.strictObject({ id: z.string().regex(sceneId), x0: metres, y0: metres, x1: metres, y1: metres, h: metres })),
      /** The quay blocks: rects at ground level (0). */
      quays: z.array(sceneRect),
      /** A retaining wall's front edge (y) and the terraces behind it, each with one stair flight. */
      terraceFront: metres,
      terraces: z.array(z.strictObject({ x: metres, width: amount, depth: amount, stairX: metres, stairWidth: amount, h: metres })),
      /** The canal between the quays: its banks (x), the coping lines (x), the stair landing's gap in the west coping. */
      river: z.strictObject({ west: metres, east: metres, coping: z.array(metres), landingGap: z.strictObject({ y: metres, half: metres }) }),
    })
    .optional(),
  placements: z.array(placementSchema),
  surfaces: z.array(surfaceSchema),
  /** Solid footprints (houses, stalls, lamps, rails): the walker keeps `walk.pad` away. A ship's rooms add their walls and furnishings (`@ab/scene` `shipLayout`). */
  colliders: z.array(colliderSchema),
  /** Water and voids: not walkable inside, except in `openings` (the canal, open at its bridges and the landing). */
  channels: z.array(z.strictObject({ x0: metres, y0: metres, x1: metres, y1: metres, openings: z.array(sceneRect) })),
  /**
   * The walking rules (`main.js`, `engine/src/navigation.js`, `engine/src/sprite-facing.js`): the body's clearance
   * (`pad`), the route grid's cell and node budget, walk and run speeds (m/s), how near a route point counts as
   * reached (the traveler's `arrive`, a resident's `residentArrive`), residents' speed, the distance at which a
   * patroller pauses for the traveler and how long it waits when blocked (s), the turn from screen to ground of
   * the keys and pad (`inputTurn`, radians: the camera looks a little west of north), and the facing's
   * hysteresis (degrees).
   */
  walk: z.strictObject({
    pad: amount,
    cell: amount,
    searchLimit: int.positive(),
    walkSpeed: amount,
    runSpeed: amount,
    arrive: amount,
    residentSpeed: amount,
    residentArrive: amount,
    residentPause: amount,
    blockedWait: amount,
    inputTurn: z.number().refine(thousandths),
    facingHysteresis: amount,
    /**
     * A body stands only where the ground is (the airship's `airship-layout.js` `canWalk`: the body's centre and 16
     * points on a circle `pad` + 0.01 m round it all on a surface); a town's ground runs everywhere inside `bounds`.
     */
    surfacesOnly: z.boolean().optional(),
  }),
  /** Where a traveler arrives (a ship room's come from its rooms' passages and its type's spawns: content lint wants one at least once resolved). */
  spawns: z.array(z.strictObject({ id: z.string().regex(sceneId), x: metres, y: metres, facing })),
  /**
   * Ways out to another scene: a portal leads to `to`'s `spawn` and fires when the traveler walks into its radius
   * (and on the action at a `door` interactable of the same id). A dungeon is not a portal: it is flown to from the
   * airship's wheel (ADR 0033). `kind: board` (the town's mooring) never fires by walking: the `board` interactable of
   * its id takes the traveler aboard. `kind: disembark` (the airship's gangplank) leaves the ship for the town it is
   * moored at, only while it is moored (the shell's rule).
   */
  portals: z.array(z.strictObject({ id: z.string().regex(sceneId), x: metres, y: metres, radius: amount, to: ref('scene'), spawn: z.string().regex(sceneId), kind: z.enum(['board', 'disembark']).optional() })),
  /**
   * Things to talk to, read, wish at, open, shop at, board or use (slices 6b and 7a act on them; `interactableKinds`).
   * A resident's `talk` has `follows`: its `target` is the resident's human and it stands where the resident stands
   * now (`x`, `y` are where it starts; `world.js` `npc` and `main.js` move the interaction with the walker). A `door`
   * fires the portal of the same id; a `shop` names its keeper (`target: human.<id>`, a resident of the scene: the
   * menu's title) and sells the market of the scene's `place` (its town). A `board` (the town's mooring) takes the
   * traveler aboard through the portal of its id (`kind: board`), and the traveler lands back at the spawn of its id.
   * A ship's furnishings (ADR 0033) have a `size` (their footprint and height: a solid one is a collider unless
   * `solid: false`), an incubator its `index`, a `fixture` its `fixture` (what the room's prefab draws there).
   */
  interactables: z.array(
    z.strictObject({
      id: z.string().regex(sceneId),
      x: metres,
      y: metres,
      radius: amount,
      kind: z.enum(interactableKinds),
      target: z.string().optional(),
      follows: z.boolean().optional(),
      size: z.strictObject({ w: amount, d: amount, h: amount }).optional(),
      solid: z.boolean().optional(),
      index: int.nonnegative().optional(),
      fixture: z.enum(shipFixtures).optional(),
    }),
  ),
  residents: z.array(
    z.strictObject({
      human: ref('human'),
      x: metres,
      y: metres,
      facing,
      patrol: z.array(scenePoint).optional(),
      /** Seconds a patroller waits at each patrol point. */
      wait: amount.optional(),
    }),
  ),
  /**
   * The airship's crew (ADR 0033, `airship-crew.js`): who works where by the time of day, moored and under way. A
   * post is a place and the work done there (`action`: reading the chart, coiling down a halyard, mending canvas,
   * the lantern watch, the helm), standing at `h` (a seat or a raised deck; the ground's height when absent), facing
   * `facing`, with a `collider` while someone works there. Each member names, per preset of `lighting.presets`, the
   * post it holds `moored` and `flying`; a preset it does not name, it is not on deck. The crew are the scene's
   * residents while present (the renderer draws them as the residents are drawn: proxies until their sheets exist);
   * their `talk` follows them.
   */
  crew: z
    .strictObject({
      posts: z.record(
        z.string().regex(sceneId),
        z.strictObject({
          x: metres,
          y: metres,
          h: metres.optional(),
          facing,
          action: z.enum(crewActions),
          collider: z.strictObject({ x0: metres, y0: metres, x1: metres, y1: metres, height: amount }).optional(),
        }),
      ),
      members: z.array(z.strictObject({ human: ref('human'), moored: z.record(z.string().regex(sceneId), z.string().regex(sceneId)), flying: z.record(z.string().regex(sceneId), z.string().regex(sceneId)) })),
    })
    .optional(),
  lights: z.array(lightSchema),
  emitters: z.array(emitterSchema),
  /** The flocks (a town's; Medium only). */
  birds: z.array(flockSchema).default([]),
  /** The canal (a town): its surface height, the plane it covers, and whether it reflects at each tier. */
  water: z
    .strictObject({
      level: metres,
      area: sceneRect,
      reflection: z.strictObject({ off: z.boolean(), medium: z.boolean() }),
      /** The cheap reflection's bridges (`canal-water.js`: y, left and right heights, rise) and ripple emitters (x, y, phase, scale). */
      bridges: z.array(z.strictObject({ y: metres, left: metres, right: metres, rise: metres })),
      ripples: z.array(z.strictObject({ x: metres, y: metres, phase: amount, scale: amount })),
      /** Lantern streaks on the water at night (the canal lamps' heads). */
      lamps: z.array(scenePoint3),
    })
    .optional(),
  /** The camera: lens, framings (named presets; the first is used), the portrait look-ahead in metres. */
  camera: z.strictObject({
    fov: amount,
    near: amount,
    far: amount,
    framing: z.string(),
    presets: z.record(z.string(), cameraPresetSchema),
    portraitLead: amount,
    leadAspectRange: amount,
  }),
  /**
   * The times of day (ADR 0033): named presets of one shape (`lightingPresetSchema`; the town's day and night,
   * the airship's dawn, morning, afternoon, twilight and evening), the one shown first (`default`), the seconds a
   * change blends over (`blend`: 0 switches at once, as the town; the airship's 1.6), where the sun stands and
   * aims (`position` from `target`, unless a preset gives its `direction`; the shadow map's size and its half
   * extents `extent` across and `depth` up and down, `extent × 28/30` when absent) and the fill light (a town's).
   * The pause menu's time row cycles the presets in this order (`ui.menu.<name>`).
   */
  lighting: z.strictObject({
    default: z.string().regex(sceneId),
    blend: amount,
    sun: z.strictObject({ position: scenePoint3, target: scenePoint3.optional(), shadowMapSize: int.positive(), extent: amount, depth: amount.optional() }),
    fill: z.strictObject({ color: hexColor, position: scenePoint3 }).optional(),
    presets: z.record(z.string().regex(sceneId), lightingPresetSchema),
  }),
  /** The lens's light zones (`light-zones.js`) over `zoneRect`; haze gathers beyond `hazeRect` (a town's walkable rectangle). The grade itself is each lighting preset's. */
  grade: z.strictObject({ zones: z.array(zoneSchema), zoneRect: sceneRect, zoneTexelsPerMetre: int.positive(), hazeRect: sceneRect.optional() }),
  /** Sun shafts and shade pools (`atmosphere.js`), a town's. */
  atmosphere: z
    .strictObject({
      shafts: z.array(z.strictObject({ x: metres, y: metres, width: amount, length: amount })),
      shade: z.array(z.strictObject({ x: metres, y: metres, hx: amount, hy: amount, strength: amount })),
    })
    .optional(),
  /** Metres per M sprite plane (a 48 × 64 cell): how tall a character stands here (ADR 0031). */
  spriteScale: amount,
  tags: z.array(z.string()).default([]),
});
export type Scene = z.infer<typeof sceneSchema>;

// gen:schemas (pnpm gen content-type inserts new schemas above this line)

// ---------------------------------------------------------------- rules (singletons)

/**
 * data/rules/type-chart.yaml: attacker element -> defender element -> percent.
 * Pairs not listed are 100. Wu Xing: the overcoming cycle is 200, the generating cycle is 50.
 */
export const typeChartSchema = z.partialRecord(z.enum(elements), z.partialRecord(z.enum(elements), percent.max(400)));
export type TypeChart = z.infer<typeof typeChartSchema>;

/** data/rules/combat.yaml: global combat numbers. */
export const combatRulesSchema = z.strictObject({
  /** Same-type bonus, percent. 120 = 1.2x. */
  stab: percent,
  /** Damage of a typeless skill against an imaginary target, percent. */
  typelessVsImaginary: percent,
  /** Power of a basic attack; skill `power` is relative to it. */
  basicAttackPower: int.positive(),
  /** SP gained per basic attack, and the cap. SP starts at 0 in every battle. */
  spPerBasicAttack: int.positive(),
  spMax: int.positive(),
  /** Turns an aetherial skill needs to charge before it fires (0 = none). */
  aetherialChargeTurns: int.min(0),
  /** Experience needed to go from level L to L + 1 is `expPerLevel × L`. */
  expPerLevel: int.positive(),
  /** Experience granted to each surviving party member per defeated enemy: `expPerDefeat × enemy level`. */
  expPerDefeat: int.positive(),
  levelCap: int.min(1).max(100),
  /** Party units taken into a dungeon, at most. */
  partySize: int.min(1).max(6),
  /**
   * Accuracy percent points per point of speed difference (attacker's effective spd minus the defender's).
   * A skill's accuracy gains `clamp(difference × speedAccuracyPerPoint, −cap, +cap)` percent points (between the two sides only);
   * basic attacks do not roll.
   */
  speedAccuracyPerPoint: int.min(0),
  /** Largest change, in percent points either way, that the speed difference makes to a skill's accuracy. Below 100, so an accuracy-100 skill never drops to 0 from speed alone. */
  speedAccuracyCap: int.min(0).max(99),
  /**
   * Natural regeneration on walking turns: a party unit whose own turns were steps, swaps or waits
   * `everyTurns` times in a row (an attack, skill, item or charge turn starts the count again) heals
   * `hp`: an amount, or a fraction of its max HP (minimum 1). Enemies never regenerate.
   */
  naturalRegen: z.strictObject({ everyTurns: int.positive(), hp: quantity }),
});
export type CombatRules = z.infer<typeof combatRulesSchema>;

/** data/rules/statuses.yaml: what each status does. */
export const statusRulesSchema = z.strictObject({
  /** Damage at the end of each of the holder's own turns, as a fraction of max HP, minimum 1. */
  burnDamage: fraction,
  poisonDamage: fraction,
  /** Chance, percent, that a paralysed unit loses its action. */
  paralysisSkipChance: percent.max(100),
  /** Chance, percent, that a confused unit's move or basic attack goes in a random direction instead. */
  confusionChance: percent.max(100),
  /** Accuracy multiplier, percent, for a blind attacker. */
  blindAccuracy: percent.max(100),
  /** Healed at the end of each of the holder's own turns while asleep, as a fraction of max HP, minimum 1. */
  sleepHeal: fraction,
});
export type StatusRules = z.infer<typeof statusRulesSchema>;

/** data/rules/dungeon.yaml: numbers for floors that are not per-dungeon. */
export const dungeonRulesSchema = z.strictObject({
  /** Item stacks the dungeon bag can hold. */
  bagSlots: int.positive(),
  /** Manhattan distance at which an enemy notices a party unit outside its room. */
  enemySightRange: int.positive(),
  /** Chance, percent, that an adjacent enemy uses a skill instead of a basic attack. */
  enemySkillChance: percent.max(100),
  /** Chance, percent, that an idle enemy wanders instead of waiting. */
  enemyWanderChance: percent.max(100),
  /** Skills a unit can hold at once. */
  skillSlots: int.positive().max(8),
  /** Chance, percent, that each dungeon-bag stack is lost when the run fails (rolled per stack on the `loot` stream). */
  defeatItemLossChance: percent.max(100),
  /** Percent of the player's money kept when a run fails. Applied by the server; game-core does not model money. */
  defeatMoneyKeptPercent: percent.max(100),
  /** The AI heals the most hurt unit of its side when that unit's HP is strictly below this percent of its max HP. */
  aiHealBelowPercent: percent.max(100),
  /** Manhattan distance within which an ally with no adjacent foe steps toward the nearest enemy instead of following the leader. */
  allyEngageRange: int.positive(),
});
export type DungeonRules = z.infer<typeof dungeonRulesSchema>;

/**
 * data/rules/new_player.yaml: what a new player is given. `starterBag`: the
 * stacks the gift puts in the dungeon bag (the item, its count). Content lint
 * keeps it a valid starting bag (`createRun`): known items that are not
 * equipment, each count within the item's `stackSize.dungeon`, each item once,
 * at most `dungeon.bagSlots` stacks. `defaultParty`: the party the clients
 * (the terminal and the browser) start a run with until the server holds the
 * account's party (roadmap step 3), in party order, in `createRun`'s party
 * format: a human with its level and equipment, or a monster with its level
 * and passive. game-core checks it when the run starts.
 */
export const newPlayerRulesSchema = z.strictObject({
  starterBag: z.array(z.strictObject({ item: ref('item'), count: int.positive() })),
  defaultParty: z
    .array(
      z.union([
        z.strictObject({ monster: ref('human'), level: int.min(1).max(100), equipment: z.strictObject({ weapon: ref('item'), armor: ref('item') }) }),
        z.strictObject({ monster: ref('monster'), level: int.min(1).max(100), passive: ref('passive') }),
      ]),
    )
    .min(1),
});
export type NewPlayerRules = z.infer<typeof newPlayerRulesSchema>;

/**
 * data/rules/ship_rooms.yaml (ADR 0033, lifted from brad-airship `data/room-types.json` and the per-type templates of
 * `data/interactables.json`): the airship's room types. A type lists the slot sizes it fits (cells, before a turn),
 * its levels (each the prefab that draws it and how many of its workstations it has: a hatchery's incubators), its
 * walls (thickness, height, the doors' clear width), its sockets, the interactable kinds it may hold, and what every
 * room of the type holds, in the room's own space (the unturned room, its origin the room's centre, x east, y north;
 * `airship-layout.js` copies its type's template into each room): its workstations (`fixtures`, each with the point
 * a body stands at to use it, `approach`), its own spawns (the bridge's helm), and how a passage through one of its
 * doors is laid (`passages`: the portal's inset from the door and its radius, the arrival spawn's inset; a `hatch`
 * to another deck, a `doorway` into the next room). A room's cell, size and turn place all of it
 * (`resolveShipScene`): nothing of a room is authored in world coordinates. Its name on screen is `ui.room.<type>`.
 * Content lint checks every room of a ship against it, and every level's prefab is a room prefab of the renderer's
 * catalog (`shipRoomPrefabIds`). Not in the content pack: game-core never reads it (the web shell gets it beside the
 * pack, as the scenes).
 */
const shipPassageSchema = z.strictObject({ inset: amount, radius: amount, spawn: amount });
export const shipRoomRulesSchema = z.record(
  z.string().regex(/^[a-z0-9_]+$/),
  z.strictObject({
    allowedSizes: z.array(z.tuple([int.positive(), int.positive()])).min(1),
    levels: z.array(z.strictObject({ level: int.positive(), prefab: z.enum(scenePrefabIds), capacity: int.positive() })).min(1),
    wall: z.strictObject({ thickness: amount, height: amount, doorWidth: amount }),
    sockets: z.array(z.strictObject({ id: z.string().regex(/^[a-z0-9_]+$/), x: metres, y: metres, h: metres })),
    interactables: z.array(z.enum(interactableKinds)).min(1),
    fixtures: z.array(
      z.strictObject({
        id: z.string().regex(sceneId),
        kind: z.enum(interactableKinds),
        x: metres,
        y: metres,
        radius: amount,
        size: z.strictObject({ w: amount, d: amount, h: amount }),
        solid: z.boolean().optional(),
        index: int.nonnegative().optional(),
        fixture: z.enum(shipFixtures).optional(),
        /** A wheel's spawn (one of the type's `spawns`: the room's instance of it). */
        target: z.string().regex(sceneId).optional(),
        approach: z.strictObject({ x: metres, y: metres, facing }),
      }),
    ),
    spawns: z.array(z.strictObject({ id: z.string().regex(sceneId), x: metres, y: metres, facing })),
    passages: z.strictObject({ hatch: shipPassageSchema, doorway: shipPassageSchema }),
  }),
);
export type ShipRoomRules = z.infer<typeof shipRoomRulesSchema>;
export type ShipRoomType = ShipRoomRules[string];

/** A point in game axes. */
type Pt = { readonly x: number; readonly y: number };
const facings = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'] as const;
export type SceneFacing = (typeof facings)[number];

/** `quarters` quarter turns clockwise seen from above: (x, y) → (y, −x) each (the prototype's (x, z) → (−z, x), z = −y). */
export const shipTurn = (p: Pt, quarters: number): Pt => {
  let { x, y } = p;
  for (let q = 0; q < ((quarters % 4) + 4) % 4; q += 1) [x, y] = [y, -x];
  return { x: x === 0 ? 0 : x, y: y === 0 ? 0 : y };
};

/** A facing turned by `quarters` quarter turns clockwise (n → e → s → w). */
export const shipTurnFacing = (f: SceneFacing, quarters: number): SceneFacing => facings[(facings.indexOf(f) + 2 * (((quarters % 4) + 4) % 4)) % 8] ?? f;

/** A room's own point in the scene (turned about the room's centre, then placed there); null when its deck is not on the grid. */
export const shipRoomToWorld = (grid: ShipGrid, room: ShipRoom, p: Pt): Pt | null => {
  const e = shipRoomExtent(grid, room);
  if (e === null) return null;
  const r = shipTurn(p, room.rotation / 90);
  return { x: r.x + e.cx, y: r.y + e.cy };
};

/** A door's centre in the room's own space, the way into the room from it (a unit step, own space) and the facing that looks that way. */
export const shipDoorLocal = (grid: ShipGrid, room: ShipRoom, door: ShipRoom['doors'][number]): { readonly x: number; readonly y: number; readonly inward: Pt; readonly facing: SceneFacing } => {
  const width = room.size[0] * grid.cell;
  const depth = room.size[1] * grid.cell;
  const horizontal = door.side === 'north' || door.side === 'south';
  const along = -(horizontal ? width : depth) / 2 + (door.offset + 0.5) * grid.cell;
  switch (door.side) {
    case 'north':
      return { x: along, y: depth / 2, inward: { x: 0, y: -1 }, facing: 's' };
    case 'south':
      return { x: along, y: -depth / 2, inward: { x: 0, y: 1 }, facing: 'n' };
    case 'east':
      return { x: width / 2, y: -along, inward: { x: -1, y: 0 }, facing: 'w' };
    case 'west':
      return { x: -width / 2, y: -along, inward: { x: 1, y: 0 }, facing: 'e' };
  }
};

/**
 * A door of a room seen as a passage: the door's centre in the scene, whether it is a `doorway` (it meets a door of
 * another room on the same deck) or a `hatch` (any other: the deck's companionways), and, laid by the type's
 * `passages`, the portal's point and radius and the arrival spawn (its id `<room>_<side>`, facing into the room).
 * `to` is the door's link (a door without one is an opening within its scene: no portal, no spawn).
 */
export type ShipPassage = {
  readonly id: string;
  readonly room: string;
  readonly side: ShipSide;
  readonly kind: 'hatch' | 'doorway';
  readonly door: Pt;
  readonly portal: Pt;
  readonly radius: number;
  readonly spawn: Pt & { readonly facing: SceneFacing };
  readonly to: { readonly scene: string; readonly spawn: string } | null;
};

/** Every door of every room of a ship as a passage (a room whose type the rules lack is left out: lint reports it). */
export const shipPassages = (ship: Ship, rules: ShipRoomRules): ShipPassage[] => {
  const g = ship.grid;
  const centres: { readonly room: ShipRoom; readonly p: Pt }[] = [];
  for (const room of ship.rooms) {
    for (const d of room.doors) {
      const c = shipRoomToWorld(g, room, shipDoorLocal(g, room, d));
      if (c !== null) centres.push({ room, p: c });
    }
  }
  const eps = 1e-6;
  return ship.rooms.flatMap((room) => {
    const type = rules[room.type];
    if (type === undefined) return [];
    const q = room.rotation / 90;
    return room.doors.flatMap((d): ShipPassage[] => {
      const local = shipDoorLocal(g, room, d);
      const door = shipRoomToWorld(g, room, local);
      if (door === null) return [];
      const kind = centres.some((c) => c.room !== room && c.room.deck === room.deck && Math.abs(c.p.x - door.x) < eps && Math.abs(c.p.y - door.y) < eps) ? 'doorway' : 'hatch';
      const lay = type.passages[kind];
      const at = (inset: number): Pt => shipRoomToWorld(g, room, { x: local.x + local.inward.x * inset, y: local.y + local.inward.y * inset }) ?? door;
      return [{ id: `${room.id}_${d.side}`, room: room.id, side: d.side, kind, door, portal: at(lay.inset), radius: lay.radius, spawn: { ...at(lay.spawn), facing: shipTurnFacing(local.facing, q) }, to: d.to ?? null }];
    });
  });
};

type SceneInteractable = Scene['interactables'][number];
/** Metres to the content's three decimals (a turned point's float noise off). */
const mm = (v: number): number => Math.round(v * 1000) / 1000 + 0;

/**
 * What a room of a ship holds, placed in the scene: its type's workstations (ids `<room>_<id>`; a wheel's `target`
 * the room's instance of the type's spawn), its type's spawns (`<room>_<id>`), and the portals and arrival spawns of
 * its doors with a `to` (`shipPassages`). Null when the room's type or deck is unknown.
 */
export const shipRoomContents = (ship: Ship, rules: ShipRoomRules, room: ShipRoom): { readonly interactables: SceneInteractable[]; readonly spawns: Scene['spawns']; readonly portals: Scene['portals'] } | null => {
  const type = rules[room.type];
  const g = ship.grid;
  if (type === undefined || shipRoomExtent(g, room) === null) return null;
  const q = room.rotation / 90;
  const place = (p: Pt): Pt => shipRoomToWorld(g, room, p) ?? p;
  const interactables = type.fixtures.map((f): SceneInteractable => {
    const p = place(f);
    return {
      id: `${room.id}_${f.id}`,
      x: mm(p.x),
      y: mm(p.y),
      radius: f.radius,
      kind: f.kind,
      size: f.size,
      ...(f.solid === undefined ? {} : { solid: f.solid }),
      ...(f.index === undefined ? {} : { index: f.index }),
      ...(f.fixture === undefined ? {} : { fixture: f.fixture }),
      ...(f.target === undefined ? {} : { target: `${room.id}_${f.target}` }),
    };
  });
  const spawns: Scene['spawns'][number][] = type.spawns.map((s) => {
    const p = place(s);
    return { id: `${room.id}_${s.id}`, x: mm(p.x), y: mm(p.y), facing: shipTurnFacing(s.facing, q) };
  });
  const portals: Scene['portals'][number][] = [];
  for (const p of shipPassages(ship, rules)) {
    if (p.room !== room.id || p.to === null) continue;
    portals.push({ id: p.id, x: mm(p.portal.x), y: mm(p.portal.y), radius: p.radius, to: p.to.scene, spawn: p.to.spawn });
    spawns.push({ id: p.id, x: mm(p.spawn.x), y: mm(p.spawn.y), facing: p.spawn.facing });
  }
  return { interactables, spawns, portals };
};

/** The rooms a ship scene shows: its deck's on its ship (only those it names, when it names some). */
export const shipSceneRooms = (scene: Scene, holder: Scene | undefined): ShipRoom[] => {
  const on = scene.onShip;
  const ship = holder?.ship;
  if (on === undefined || ship === undefined) return [];
  return ship.rooms.filter((r) => r.deck === on.deck && (on.rooms === undefined || on.rooms.includes(r.id)));
};

/**
 * A ship scene with what its rooms hold (ADR 0033): the scene's own interactables, spawns and portals (what is not a
 * room's: the crew's talk, the gangplank, the deck's companionways) plus every shown room's contents
 * (`shipRoomContents`). Any other scene, or one whose ship or rules are missing, comes back as it is. The shell, the
 * renderer and content lint all read the resolved scene.
 */
export const resolveShipScene = (scene: Scene, holder: Scene | undefined, rules: ShipRoomRules | undefined): Scene => {
  const ship = holder?.ship;
  if (rules === undefined || ship === undefined) return scene;
  const rooms = shipSceneRooms(scene, holder);
  if (rooms.length === 0) return scene;
  const interactables = [...scene.interactables];
  const spawns = [...scene.spawns];
  const portals = [...scene.portals];
  for (const room of rooms) {
    const c = shipRoomContents(ship, rules, room);
    if (c === null) continue;
    interactables.push(...c.interactables);
    spawns.push(...c.spawns);
    portals.push(...c.portals);
  }
  return { ...scene, interactables, spawns, portals };
};

/**
 * data/rules/routes.yaml (ADR 0033): where the airship flies from a town, how long the flight takes and the fuel it
 * burns. The wheel's menu lists the routes from the town the ship is moored at. The flight timer and fuel are roadmap
 * step 6's: until then the menu shows no cost and the flight is the banner's time. Not in the content pack (game-core
 * never reads it).
 */
export const routesRulesSchema = z.strictObject({
  routes: z.array(z.strictObject({ from: ref('town'), to: z.union([ref('dungeon'), ref('town')]), minutes: int.positive(), fuel: int.positive() })).min(1),
});
export type RoutesRules = z.infer<typeof routesRulesSchema>;

/** A skip ticket tier's id: an asset name in the books (`skip:<id>`) and a string key (`ui.ticket.<id>`). */
export const ticketTierId = z.string().regex(/^[a-z0-9_]{1,32}$/);

/**
 * data/rules/timers.yaml (ADR 0039 slice 8c, ADR 0040 slice 8e, ADR 0042 slice 8h). `skipTickets`: the tiers of skip
 * tickets (Q82, decided 2026-10-05; Q88, 2026-10-09), each covering `minutes` of a timer: at least one tier, unique
 * ids, positive whole minutes, sorted from the shortest up. Skipping a timer spends tickets whose minutes add up to at
 * least the time left; the server offers the combination that wastes the least (`packages/server/src/skip-price.ts`)
 * and the player may choose another that covers it (ADR 0042). `gameDayStartsAt`: when a new game day begins (the daily
 * login reward), a local time `HH:MM` in each account's own time zone (Q87, ADR 0042; a day with a summer-time change
 * is 23 or 25 hours long). Not in the content pack (game-core never reads it).
 */
export const timersRulesSchema = z
  .strictObject({
    skipTickets: z.array(z.strictObject({ id: ticketTierId, minutes: int.positive() })).min(1),
    gameDayStartsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'gameDayStartsAt is HH:MM'),
  })
  .superRefine((t, ctx) => {
    const ids = t.skipTickets.map((s) => s.id);
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: 'custom', path: ['skipTickets'], message: 'a tier id is listed twice' });
    t.skipTickets.forEach((s, i) => {
      const before = t.skipTickets[i - 1];
      if (before !== undefined && before.minutes >= s.minutes) ctx.addIssue({ code: 'custom', path: ['skipTickets', i], message: `tiers are sorted by minutes, shortest first (${s.id} after ${before.id})` });
    });
  });
export type TimersRules = z.infer<typeof timersRulesSchema>;

/**
 * The conditions a dungeon's skip-ticket reward can have (Q83, ADR 0040 D3): a closed list the server grows. Today
 * `first_clear` (the account's first completed run of the dungeon) and `clear` (every completed run). The designer
 * wants more (a number of turns, a task, how well it went): each new one is one more variant here and one more case in
 * the server's `rewards.ts`, for example `{ when: turns_under, turns: n, tickets }` or `{ when: floors_cleared,
 * floors: n, tickets }`.
 */
export const dungeonRewardSchema = z.discriminatedUnion('when', [
  z.strictObject({ when: z.literal('first_clear'), tickets: z.record(ticketTierId, int.positive()) }),
  z.strictObject({ when: z.literal('clear'), tickets: z.record(ticketTierId, int.positive()) }),
]);
export type DungeonReward = z.infer<typeof dungeonRewardSchema>;

/**
 * data/rules/rewards.yaml (ADR 0040, slice 8e; Q83): skip tickets from play, by tier id. `dailyLogin` on an account's
 * first request of a game day, `firstHatch` with its first hatch, and per dungeon (by id) a list of conditions, each
 * matching one granted. A dungeon not listed gives none. Outside the content hash a run pins (the server reads it; a
 * dungeon's own file would be in that hash), so balancing a reward voids no run in flight.
 */
export const rewardsRulesSchema = z.strictObject({
  dailyLogin: z.record(ticketTierId, int.positive()),
  firstHatch: z.record(ticketTierId, int.positive()),
  dungeons: z.record(z.string().regex(/^dungeon\.[a-z0-9_]+$/), z.array(dungeonRewardSchema).min(1)),
});
export type RewardsRules = z.infer<typeof rewardsRulesSchema>;

/**
 * data/rules/storage.yaml (ADR 0039, slice 8c): the airship's storage holds `slots` stacks, each of one item and at
 * most its `stackSize.airship` (a placeholder until the airship's storage rooms decide it). Not in the content pack.
 */
export const storageRulesSchema = z.strictObject({ slots: int.positive() });
export type StorageRules = z.infer<typeof storageRulesSchema>;

/** Singleton rule files under data/rules/. One file each. */
export const rules = {
  typeChart: { file: 'type-chart', label: 'Type chart', schema: typeChartSchema },
  combat: { file: 'combat', label: 'Combat rules', schema: combatRulesSchema },
  statuses: { file: 'statuses', label: 'Status rules', schema: statusRulesSchema },
  dungeon: { file: 'dungeon', label: 'Dungeon rules', schema: dungeonRulesSchema },
  newPlayer: { file: 'new_player', label: 'New-player gift', schema: newPlayerRulesSchema },
  shipRooms: { file: 'ship_rooms', label: 'Airship rooms', schema: shipRoomRulesSchema },
  routes: { file: 'routes', label: 'Flight routes', schema: routesRulesSchema },
  timers: { file: 'timers', label: 'Timers', schema: timersRulesSchema },
  storage: { file: 'storage', label: 'Airship storage', schema: storageRulesSchema },
  rewards: { file: 'rewards', label: 'Rewards from play', schema: rewardsRulesSchema },
} as const;
export type RuleName = keyof typeof rules;

/**
 * Everything game-core needs, indexed by ID, plus the hash that runs pin
 * (ADR 0002). Built by `@ab/content`'s `toContentPack` from validated files;
 * game-core only reads it.
 */
export type ContentPack = {
  readonly hash: string;
  readonly monsters: Readonly<Record<string, Monster>>;
  readonly skills: Readonly<Record<string, Skill>>;
  readonly passives: Readonly<Record<string, Passive>>;
  readonly items: Readonly<Record<string, Item>>;
  readonly dungeons: Readonly<Record<string, Dungeon>>;
  readonly humans: Readonly<Record<string, Human>>;
  /** Dungeon looks, for the renderer; game-core never reads them (the hash still covers them). */
  readonly themes: Readonly<Record<string, Theme>>;
  readonly rules: {
    readonly typeChart: TypeChart;
    readonly combat: CombatRules;
    readonly statuses: StatusRules;
    readonly dungeon: DungeonRules;
    readonly newPlayer: NewPlayerRules;
  };
};

// ---------------------------------------------------------------- registry

/**
 * Every content type the game knows. `dir` is the folder under
 * `packages/content/data/`; one YAML file per entity, named after the part of
 * the ID following the prefix (`monster.slime` lives in `monsters/slime.yaml`).
 */
export const entityTypes = {
  monster: { dir: 'monsters', label: 'Monsters', schema: monsterSchema },
  skill: { dir: 'skills', label: 'Skills', schema: skillSchema },
  passive: { dir: 'passives', label: 'Passives', schema: passiveSchema },
  item: { dir: 'items', label: 'Items', schema: itemSchema },
  town: { dir: 'towns', label: 'Towns', schema: townSchema },
  dungeon: { dir: 'dungeons', label: 'Dungeons', schema: dungeonSchema },
  human: { dir: 'humans', label: 'Humans', schema: humanSchema },
  theme: { dir: 'themes', label: 'Themes', schema: themeSchema },
  scene: { dir: 'scenes', label: 'Scenes', schema: sceneSchema },
  // gen:entityTypes
} as const;
export type EntityType = keyof typeof entityTypes;

/** Text keys every entity of a type must have in every locale. */
export const requiredStrings: Record<EntityType, readonly string[]> = {
  monster: ['name', 'desc'],
  skill: ['name', 'desc'],
  passive: ['name', 'desc'],
  item: ['name', 'desc'],
  town: ['name', 'desc'],
  dungeon: ['name', 'desc'],
  human: ['name', 'desc'],
  theme: ['name'],
  scene: ['name'],
  // gen:requiredStrings
};

export const assetKinds = ['sprite-sheet', 'portrait', 'icon', 'model', 'texture', 'sfx', 'bgm'] as const;
export type AssetKind = (typeof assetKinds)[number];

/** Which wiki section tracks an asset kind. */
export const assetDiscipline: Record<AssetKind, 'visual' | 'music'> = {
  'sprite-sheet': 'visual',
  portrait: 'visual',
  icon: 'visual',
  model: 'visual',
  texture: 'visual',
  sfx: 'music',
  bgm: 'music',
};

/** Assets each entity of a type needs. This is what makes "not started yet" visible. */
export const requiredAssets: Record<EntityType, readonly { slot: string; kind: AssetKind }[]> = {
  monster: [
    { slot: 'sprite', kind: 'sprite-sheet' },
    { slot: 'portrait', kind: 'portrait' },
    { slot: 'egg', kind: 'sprite-sheet' },
    { slot: 'cry', kind: 'sfx' },
  ],
  skill: [{ slot: 'icon', kind: 'icon' }],
  passive: [{ slot: 'icon', kind: 'icon' }],
  item: [{ slot: 'icon', kind: 'icon' }],
  town: [
    { slot: 'banner', kind: 'texture' },
    { slot: 'theme', kind: 'bgm' },
  ],
  dungeon: [],
  human: [
    { slot: 'sprite', kind: 'sprite-sheet' },
    { slot: 'portrait', kind: 'portrait' },
  ],
  theme: [],
  scene: [],
  // gen:requiredAssets
};

/**
 * Asset slots an entity of a type may have but need not (ADR 0031): a scene's
 * surface textures. A slot here counts only when its entity has a record for
 * it (the report and the wiki list recorded optional slots; a missing one is
 * not "not started"). A town's textures live on its town scene; an interior
 * scene draws with its town's (`@ab/renderer` takes the texture URLs it is
 * given).
 */
export const optionalAssets: Partial<Record<EntityType, readonly { slot: string; kind: AssetKind }[]>> = {
  scene: [
    { slot: 'paving', kind: 'texture' },
    { slot: 'limestone', kind: 'texture' },
    { slot: 'slate_roof', kind: 'texture' },
    { slot: 'terracotta_roof', kind: 'texture' },
    { slot: 'rug', kind: 'texture' },
    { slot: 'caustics', kind: 'texture' },
    // The airship's painted atlases (ADR 0033): its clutter cards (the deck's), the egg and hatch frames (the hatchery's).
    // The crew are characters: they come from the sprites repo, never from a scene's texture (ADR 0033 D8).
    { slot: 'clutter', kind: 'texture' },
    { slot: 'hatch', kind: 'texture' },
  ],
};

export const assetStatuses = ['todo', 'wip', 'review', 'final', 'placeholder'] as const;
export type AssetStatus = (typeof assetStatuses)[number];

export const assetRecordSchema = z
  .strictObject({
    status: z.enum(assetStatuses),
    owner: z.string().min(1).optional(),
    provenance: z.enum(['human', 'ai-assisted', 'purchased']).optional(),
    license: z.string().min(1).optional(),
    reviewedBy: z.string().min(1).optional(),
    /** Path inside the shared raw-assets folder. */
    raw: z.string().min(1).optional(),
    /** Path of the processed, shipped file, relative to the game repo's asset folder. Set by the asset pipeline tool when the asset ships; absent means not shipped (ADR 0028). */
    file: z.string().min(1).optional(),
    notes: z.string().optional(),
  })
  .superRefine((r, ctx) => {
    if (r.status !== 'todo' && !r.provenance) {
      ctx.addIssue({ code: 'custom', message: 'provenance is required once work has started' });
    }
    if (r.status === 'final' && !r.reviewedBy) {
      ctx.addIssue({ code: 'custom', message: 'final assets need reviewedBy (a human reviewer)' });
    }
    if (r.provenance === 'purchased' && !r.license) {
      ctx.addIssue({ code: 'custom', message: 'purchased assets need license' });
    }
    // `pnpm assets ship` sets `file` on a `final` slot. The one other record with a file is a hand-shipped
    // placeholder (ADR 0031: the town's prototype textures), which must say where it came from in `notes`.
    if (r.file !== undefined && r.status !== 'final' && (r.status !== 'placeholder' || !r.notes)) {
      ctx.addIssue({ code: 'custom', message: 'only a final record (set by pnpm assets ship) or a placeholder with notes (hand-shipped) has a file' });
    }
  });
export type AssetRecord = z.infer<typeof assetRecordSchema>;

/** `packages/content/assets/<entityId>.yaml`: slot name → record. */
export const entityAssetsSchema = z.strictObject({
  entity: z.string(),
  assets: z.record(z.string(), assetRecordSchema),
});

/**
 * The sprite export contract, lifted from the sprites repo
 * (`aerodynamics-boy-sprites`: `contracts/monster.md` §5, `contracts/human.md`
 * §4, `contracts/common.md` §4 and `contracts/spec.json`, version 1): the JSON
 * `tools/sprites.py export` writes next to each PNG. The PNG is one row of
 * frames (a plain grid), and `frames` (each frame's top-left corner) is the
 * only layout truth. `directions` lists the facings actually drawn; `mirror`
 * lists only the pairs whose target is not drawn. The asset pipeline tool
 * (`pnpm assets`) and the web client's dev fallback validate every export
 * with this schema before reading it; the renderer's loader (`sprites.ts`)
 * reads the result.
 */
export const spriteDirs = ['s', 'se', 'e', 'ne', 'n', 'nw', 'w', 'sw'] as const;
export type SpriteDir = (typeof spriteDirs)[number];
const spriteDir = z.enum(spriteDirs);
const pixelPair = z.tuple([int.nonnegative(), int.nonnegative()]);
const spriteAnimSchema = z.strictObject({
  loop: z.boolean(),
  /** The impact (attack) or release (cast) frame, an index into each direction's `frames`. */
  hitFrame: int.nonnegative().optional(),
  /** Per drawn direction (or `s` alone for an anim without directions): indices into the export's `frames`, and each frame's duration. */
  dirs: z.partialRecord(spriteDir, z.strictObject({ frames: z.array(int.nonnegative()).min(1), ms: z.array(int.positive()).min(1) })),
});
/** The anims a body sprite (`sprite` slot) must draw, per contract: `contracts/spec.json` `prototypeRequired`. */
export const spritePrototypeRequired: Readonly<Record<'monster@1' | 'human@1', readonly string[]>> = {
  'monster@1': ['idle', 'walk', 'attack', 'hurt', 'faint'],
  'human@1': ['idle'],
};
export const spriteExportSchema = z
  .strictObject({
    contract: z.enum(['monster@1', 'human@1']),
    id: z.string().regex(/^(monster|human)\.[a-z0-9_]+$/),
    slot: z.string().min(1),
    /** `S`, `M`, `L`, `XL` for a body; `egg` and `portrait` for those slots. */
    sizeClass: z.string().min(1),
    cell: z.tuple([int.positive(), int.positive()]),
    /** The ground contact point in the cell (x = width / 2, y = height − 3); null for a portrait. */
    pivot: pixelPair.nullable(),
    pxPerMetre: int.positive(),
    directions: z.array(spriteDir).min(1),
    mirror: z.partialRecord(z.enum(['sw', 'w', 'nw']), z.enum(['se', 'e', 'ne'])),
    anims: z.record(z.string(), spriteAnimSchema),
    frames: z.array(pixelPair).min(1),
    sheet: z.tuple([int.positive(), int.positive()]),
    status: z.enum(assetStatuses),
    provenance: z.enum(['human', 'ai-assisted', 'purchased']),
  })
  .superRefine((e, ctx) => {
    const issue = (message: string): void => {
      ctx.addIssue({ code: 'custom', message });
    };
    const drawn: readonly string[] = e.directions;
    if (e.id.split('.')[0] !== e.contract.split('@')[0]) issue(`id ${e.id} does not match contract ${e.contract}`);
    if (new Set(e.directions).size !== e.directions.length) issue('directions lists a facing twice');
    if (e.slot === 'sprite') {
      for (const d of ['s', 'e', 'n']) if (!drawn.includes(d)) issue(`directions must include s, e and n (the prototype minimum); ${d} is missing`);
      if (e.pivot === null) issue('a body sprite needs a pivot');
      // Every anim a body sheet lacks falls back to idle, so idle must be drawn facing s at least (the loader's fallback).
      for (const name of spritePrototypeRequired[e.contract]) if (!Object.hasOwn(e.anims, name)) issue(`anims.${name} is missing (contracts/spec.json prototypeRequired for a ${e.contract} body sprite)`);
      if (Object.hasOwn(e.anims, 'idle') && e.anims['idle']?.dirs.s === undefined) issue('anims.idle has no s facing (every missing anim falls back to it)');
    }
    for (const target of Object.keys(e.mirror)) {
      if (drawn.includes(target)) issue(`mirror.${target}: ${target} is drawn, so it is not mirrored (mirror lists only the pairs whose target is not drawn)`);
    }
    const [sheetW, sheetH] = e.sheet;
    e.frames.forEach(([x, y], i) => {
      if (x + e.cell[0] > sheetW || y + e.cell[1] > sheetH) issue(`frames[${i}] lies outside the sheet`);
    });
    for (const [name, anim] of Object.entries(e.anims)) {
      const runs = Object.entries(anim.dirs);
      if (runs.length === 0) issue(`anims.${name} has no directions`);
      for (const [dir, run] of runs) {
        if (dir !== 's' && !drawn.includes(dir)) issue(`anims.${name}.dirs.${dir}: ${dir} is not drawn`);
        if (run.frames.length !== run.ms.length) issue(`anims.${name}.dirs.${dir}: frames and ms differ in length`);
        if (run.frames.some((f) => f >= e.frames.length)) issue(`anims.${name}.dirs.${dir}: a frame index is past the end of frames`);
        if (anim.hitFrame !== undefined && anim.hitFrame >= run.frames.length) issue(`anims.${name}.hitFrame is past the end of ${dir}'s frames`);
      }
    }
  });
export type SpriteExport = z.infer<typeof spriteExportSchema>;

/**
 * One sprite sheet as a client receives it: the entity it draws, its export
 * JSON, where to fetch the PNG, and whether that PNG is the shipped file in
 * `packages/content/files/` or the sprites repo's export served by the dev
 * fallback (ADR 0028 D4: never in a release build).
 */
export type SpriteSource = {
  readonly id: string;
  readonly meta: SpriteExport;
  readonly url: string;
  readonly origin: 'shipped' | 'fallback';
};

export const stringTableSchema = z.record(z.string(), z.string());

/**
 * String keys come in two kinds: entity text, `<entityId>.<field>` (see
 * `requiredStrings`), and UI chrome, `ui.<component>.<name>` (`ui.status.hp`,
 * `ui.help.keys`), which belongs to no entity. Content lint checks both.
 */
export const uiStringKey = /^ui\.[a-z0-9_]+\.[a-z0-9_]+$/;

/**
 * A text's `{placeholders}`: lower-case names in braces (`{unit}`, `{amount}`),
 * filled by `@ab/session`'s `formatText`. A translation must use the same
 * placeholders as the English text of its key, each as many times (content lint and `pnpm strings import`
 * check it), so a translation can reorder the words but never break a template.
 */
const placeholder = /\{([a-z_]+)\}/g;

/** The placeholder names of a text, sorted, a name as often as it appears (a multiset: `{unit} {unit}` is not `{unit}`). */
export const placeholdersOf = (text: string): string[] => [...text.matchAll(placeholder)].map((m) => m[1] ?? '').sort();

/** `text` with each placeholder replaced by `word(name)`; a placeholder with no word stays as written. */
export const fillPlaceholders = (text: string, word: (name: string) => string | undefined): string =>
  text.replace(placeholder, (whole: string, name: string) => word(name) ?? whole);

/** A translated key's record in `strings/<locale>/status.json`, the file `pnpm strings` owns (never edited by hand). */
export const stringStatusEntrySchema = z.strictObject({
  /** sha256 of the English text the translation was made from, first 12 hex digits. */
  source: z.string().regex(/^[0-9a-f]{12}$/),
  /** sha256 of the translated text the tool last wrote, first 12 hex digits; a different current text is a human's edit. */
  text: z.string().regex(/^[0-9a-f]{12}$/),
  by: z.enum(['ai', 'human']),
  /** ISO date (YYYY-MM-DD) of the write or mark. */
  at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  note: z.string().optional(),
});
export type StringStatusEntry = z.infer<typeof stringStatusEntrySchema>;
export const stringStatusSchema = z.record(z.string(), stringStatusEntrySchema);

export const releaseSnapshotSchema = z.strictObject({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  date: z.string(),
  contentHash: z.string(),
  notes: z.string().optional(),
  entities: z.record(z.string(), z.unknown()),
});
export type ReleaseSnapshot = z.infer<typeof releaseSnapshotSchema>;
